/*!
 * mm-app.js — the mashup maker's page: two uploads, the fit verdict, the run,
 * the workspace (timeline + panels) and the downloads. Strings arrive from the
 * blade as window.__MM.t, configuration as window.__MM.cfg.
 *
 * The daily unit is the PAIR of songs: one composite key 'set:A~B' (split-screen
 * pattern) — a per-file unit at a cap of 2 would spend both runs on one mashup.
 * Charged when the mashup is on screen; everything after that (other key, other
 * bar, another format) is the same unit, and remaking the same pair today is free.
 * A second vocal (deck C) rides on the same unit: it is an addition to THIS mashup,
 * not a new one — the key stays the pair's.
 *
 * Three more doors since 25.09: deck C (a second singer over the same music), the pair
 * finder (mm-pair.js — which of my songs go together) and «Video for Reels» (the
 * loudest-vocal ~30 s of the mashup carried to /audio-to-video with its vertical template).
 */
(function () {
    'use strict';
    var MM = window.__MM || {}, T = MM.t || {}, CFG = MM.cfg || {};
    var SLUG = 'mashup-maker';
    var $ = function (id) { return document.getElementById(id); };
    function t(k, rep) { var s = T[k] || k; if (rep) Object.keys(rep).forEach(function (r) { s = s.split(':' + r).join(String(rep[r])); }); return s; }
    function feature(f, d) { try { window.__aeFeature && window.__aeFeature(f, d || {}); } catch (_) {} }
    function stage(n, d) { try { window.__stage && window.__stage(n, d || {}); } catch (_) {} }
    function fmtTime(s) { s = Math.max(0, s || 0); var m = Math.floor(s / 60), r = Math.floor(s % 60); return m + ':' + (r < 10 ? '0' : '') + r; }
    function fmtEta(sec) {
        var min = Math.max(1, Math.round(sec / 60));
        try { return new Intl.NumberFormat(window.__LOCALE || 'en', { style: 'unit', unit: 'minute', unitDisplay: 'short' }).format(min); } catch (_) { return min + ' min'; }
    }
    // Western digits like the rest of the copy (Intl gives Arabic-Indic ones for `ar`) and a real
    // minus sign, as signed() writes it; the sign of a change is ours, not the locale's hyphen
    function fmtPct(x) {
        var a = Math.abs(x), s;
        try { s = new Intl.NumberFormat(window.__LOCALE || 'en', { style: 'percent', maximumFractionDigits: 0, numberingSystem: 'latn' }).format(a); } catch (_) { s = Math.round(a * 100) + '%'; }
        return s;
    }
    function fmtPctSigned(x) { var r = Math.round(x * 100); return (r > 0 ? '+' : r < 0 ? '−' : '') + fmtPct(x); }
    function signed(n) { n = Math.round(n * 10) / 10; return (n > 0 ? '+' : n < 0 ? '−' : '') + Math.abs(n); }

    var ctx = null, engine = null, mixer = null, timeline = null;
    var files = { a: null, b: null, c: null }, keys = { a: null, b: null, c: null }, ready = { a: false, b: false, c: false };
    var off = null, running = null, runGen = 0, built = false, pendingMove = null;
    var fmt = 'wav', loud = 'match';
    // a three-song mashup: deck C is open (cOn); the studio edits ONE vocal at a time (A or C)
    var cOn = false, editWhich = 'a';
    function withC() { var r = engine && engine.rendered(); return !!(r && r.vocal2 && engine.planC()); }
    // the console's two modes live on its root: setup (the decks, the verdict, the button) and the
    // studio (the decks shrink to their file rows and the result takes the screen) — CSS reads these
    var ROOT = $('mmRoot'), PAID = CFG.paid || {};
    function mode(cls, on) { if (ROOT) ROOT.classList.toggle(cls, !!on); }
    function deckEl(k) { return $('mmDeck' + k.toUpperCase()); }

    function audioCtx() {
        if (!ctx) { var AC = window.AudioContext || window.webkitAudioContext; ctx = new AC({ latencyHint: 'playback' }); }
        return ctx;
    }
    /** A song's sound decoded again from its file — a phone lets go of it after the server's stems
     *  (MashupEngine releaseSongs), and a swap, an «a cappella» tick or a tempo check brings it back. */
    function redecodeSong(st) {
        var R = window.AudioDecoderRobust;
        if (!st || !st.file || !R || !R.robust) return Promise.resolve(null);
        return R.robust(st.file, null, { slug: SLUG }).then(function (r) { return r && r.buffer; });
    }
    function getEngine() {
        if (!engine) engine = window.MashupEngine.create({ slug: SLUG, ctx: audioCtx(), redecode: redecodeSong });
        return engine;
    }

    // ── first screen ───────────────────────────────────────────────────────
    function slotEl(k, part) { return $('mm' + part + k.toUpperCase()); }
    // the beat model's download (41 MB on a graphics card, 5 MB otherwise) is said in the busy slots:
    // «Слушаю…» with no number for a minute on a slow line reads as a hang
    var modelDl = null;
    function busyText() {
        if (modelDl && modelDl.total > 0 && modelDl.got < modelDl.total) return t('analyzing_model', { got: Math.round(modelDl.got / 1048576), total: Math.round(modelDl.total / 1048576) });
        return t('analyzing');
    }
    // ── how far the listening is, said while it lasts ──────────────────────────
    // A song is minutes of work on a phone without a graphics card (bug report 2026-09-27: two
    // songs, 25 minutes, «Слушаю доли…» with no number and a grey button with no reason — the
    // person removed and re-picked both files, which made it longer). Each busy slot now says its
    // share done; the note by the button says what it waits for and how long.
    var slotProg = { a: null, b: null, c: null };
    function busySlots() { return ['a', 'b', 'c'].filter(function (k) { var s = slotEl(k, 'Stats'); return s && !s.hidden && s.classList.contains('is-busy'); }); }
    /**
     * Seconds left for every song still being listened to — asked of the scheduler, which timed every
     * model chunk on this device and knows how many workers take them (MashupAnalysis.estimate). A
     * guess from the progress events came out at 0.66 of the truth on the slow-phone stand: chunks
     * arrive two at a time, and one already under way when a song is picked again ends «too fast».
     */
    function listenEta() {
        var An = window.MashupAnalysis;
        if (!An || !An.estimate) return null;
        var songs = busySlots().map(function (k) {
            var p = slotProg[k], st = engine && engine.song(k);
            var of = p && p[1] ? p[1] : (st && An.chunksFor ? An.chunksFor(st.duration) : 0);
            return { done: p ? p[0] : 0, of: of, cut: !!(p && p[1]) };
        });
        return songs.length ? An.estimate(songs) : null;
    }
    /** The slot's song is no longer being listened to (numbers painted, a message, cleared, failed). */
    function slotIdle(k) { var s = slotEl(k, 'Stats'); if (s) { s.classList.remove('is-busy'); s.removeAttribute('aria-busy'); } slotProg[k] = null; syncListenRun(); }
    function slotBusyHtml(k) {
        var p = slotProg[k], txt = busyText(), others = busySlots().filter(function (x) { return x !== k && slotProg[x] && slotProg[x][0] > 0; });
        if (!(modelDl && modelDl.total > 0 && modelDl.got < modelDl.total)) {
            // one worker takes one song at a time: the other waits its turn, and says so
            if (p && p[1] && p[0] > 0) txt = t('analyzing_pct', { pct: fmtPct(p[0] / p[1]) });
            else if (others.length) txt = t('analyzing_queued', { k: others[0].toUpperCase() });
        }
        return '<p class="mm-analyzing">' + txt + '</p>';
    }
    function paintListening() {
        busySlots().forEach(function (k) { var s = slotEl(k, 'Stats'), p = s.querySelector('.mm-analyzing'); if (p) p.outerHTML = slotBusyHtml(k); });
        paintGoWait();
    }
    function setSlotBusy(k, on) {
        var s = slotEl(k, 'Stats'); s.hidden = false; s.classList.toggle('is-busy', !!on);
        // one announcement when the numbers are there, not one per chunk
        if (on) s.setAttribute('aria-busy', 'true'); else s.removeAttribute('aria-busy');
        if (on) { slotProg[k] = null; s.innerHTML = slotBusyHtml(k); }
        syncListenRun();
    }
    function onSlotProgress(k, done, of) { slotProg[k] = [done, of]; paintListening(); }
    // the note by the grey button: what it waits for and how long (restored when nothing is listened to)
    var goHintText = null;
    function paintGoWait() {
        var hint = $('mmGoHint'); if (!hint) return;
        if (goHintText == null) goHintText = hint.textContent;
        var waiting = busySlots().length && slotFile('a') && slotFile('b');
        if (!waiting) { if (hint.dataset.wait) { hint.textContent = goHintText; delete hint.dataset.wait; } return; }
        var sec = listenEta(), txt = sec ? t('go_wait_eta', { t: fmtEta(sec) }) : t('go_wait');
        if (isMobile()) txt += ' ' + t('keep_open');
        hint.textContent = txt; hint.dataset.wait = '1'; hint.dataset.eta = sec == null ? '' : String(sec);
    }
    // the listening is WORK in the site's sense: the screen stays on, leaving asks first, and a tab
    // the phone kills mid-way is said on the next visit — the run bracket every capped tool gets
    var listenRun = null, listenTick = null;
    function syncListenRun() {
        var on = busySlots().length > 0;
        if (on && !listenRun) {
            try { listenRun = window.__runMark ? window.__runMark.begin() : function () {}; } catch (_) { listenRun = function () {}; }
            // the countdown moves between two chunks (a minute apart on a slow phone)
            listenTick = setInterval(paintGoWait, 5000);
        } else if (!on && listenRun) {
            try { listenRun(); } catch (_) {}
            listenRun = null;
            clearInterval(listenTick); listenTick = null;
        }
        paintGoWait();
    }
    try {
        window.MashupAnalysis && window.MashupAnalysis.onModelProgress(function (got, total) {
            modelDl = { got: got, total: total };
            paintListening();
            if (pairF && pairF.running()) { var ps = $('mmPairStatus'); if (ps && modelDl && modelDl.got < modelDl.total) ps.textContent = busyText(); }
        });
    } catch (_) {}
    /** A message that stays in the slot (a toast is gone in four seconds and the slot looked idle). */
    function slotMsg(k, text) { var s = slotEl(k, 'Stats'); s.hidden = false; s.innerHTML = '<p class="tc-msg warn">' + text + '</p>'; slotIdle(k); }
    /** One reading of the deck — a number the eye finds first, its unit, a quiet note. The label is
     *  the pill's title for a reader and a screen reader; «120 BPM» already says what it is. */
    function pill(label, value, unit, sub, extra) {
        return '<span class="mm-m' + (extra ? ' ' + extra.cls : '') + '"' + (extra && extra.style ? ' style="' + extra.style + '"' : '') + ' title="' + label + '" role="group" aria-label="' + label + '">'
            + '<b dir="ltr">' + value + '</b>' + (unit ? '<span class="u" dir="ltr">' + unit + '</span>' : '') + (sub ? '<span>' + sub + '</span>' : '') + '</span>';
    }
    /** The Camelot wheel as colour: neighbours on the wheel (the keys that mix) get neighbouring hues,
     *  so two decks that fit LOOK close — the way DJ software paints keys. */
    function camelotHue(cam) { var n = parseInt(cam, 10); return isFinite(n) ? ((n - 1) * 30 + 200) % 360 : 215; }
    function isSilent(st) {
        var r = st && st.mix && st.mix.rms && st.mix.rms.data; if (!r || !r.length) return false;
        var mx = 0; for (var i = 0; i < r.length; i++) if (r[i] > mx) mx = r[i];
        return mx < 3e-4;                                           // −70 dBFS everywhere: nothing to hear
    }
    var SRC_LABEL = { name: 'tempo_src_name', tag: 'tempo_src_tag', manual: 'tempo_src_manual', tap: 'tempo_src_tap', guess: 'tempo_src_guess' };
    function paintSlot(k) {
        var st = getEngine().song(k), box = slotEl(k, 'Stats');
        if (!st || !st.grid) { box.hidden = true; slotIdle(k); return; }
        if (isSilent(st)) { slotMsg(k, t('err_silent')); return; }
        var g = st.grid, key = st.mix && st.mix.key, K = k.toUpperCase();
        var acap = st.isStem && k !== 'b';                       // A and C are singers
        var noGrid = !g.beats.length || (g.confidence < 0.35 && !g.manual);
        var guess = acap && st.tempoSrc === 'guess';
        var bpm = noGrid && !acap ? t('no_grid') : ((guess ? '≈ ' : '') + (Math.round(g.bpm * 10) / 10));
        var srcKey = st.isStem ? SRC_LABEL[st.tempoSrc] : (st.tempoSrc === 'manual' || st.tempoSrc === 'tap' ? SRC_LABEL[st.tempoSrc] : null);
        var html = '<div class="mm-meta">'
            + pill(t('stat_bpm'), bpm, noGrid && !acap ? '' : 'BPM', srcKey ? t(srcKey) : (g.meter && g.meter !== 4 ? g.meter + '/4' : ''))
            + pill(t('stat_key'), key ? key.name : '—', '', key ? '<span class="cam" dir="ltr">' + key.camelot + '</span>' : '', key ? { cls: 'key', style: '--cam-h:' + camelotHue(key.camelot) } : null)
            + pill(t('stat_len'), fmtTime(st.duration), '', g.downIdx ? t('stat_bars', { n: g.downIdx.length }) : '')
            + '</div>';
        if (acap || noGrid) {
            // one wrapping row: the number with its unit inside the field (the pill above already
            // names what it is), apply, ×2 ÷2, and for an a cappella the metronome and tapping
            html += '<div class="mm-bpm"><label class="mm-bpm-f"><span class="sr-only">' + t('bpm_manual') + '</span><input class="tc-in" type="number" min="40" max="240" step="0.01" inputmode="decimal" id="mmBpm' + K + '" value="' + (g.bpm ? Math.round(g.bpm * 100) / 100 : '') + '" dir="ltr"><span class="mm-bpm-u" aria-hidden="true">BPM</span></label><button type="button" class="tc-btn ghost sm" data-bpm-apply="' + k + '">' + t('bpm_apply') + '</button>';
            if (acap) html += '<button type="button" class="tc-btn ghost sm" data-bpm-scale="2" aria-label="' + t('bpm_x2_aria') + '" dir="ltr">×2</button><button type="button" class="tc-btn ghost sm" data-bpm-scale="0.5" aria-label="' + t('bpm_half_aria') + '" dir="ltr">÷2</button>'
                + '<button type="button" class="tc-btn ghost sm" id="mmCheck' + K + '" aria-pressed="false">' + t('tempo_check') + '</button>'
                + '<button type="button" class="tc-btn ghost sm" id="mmTap' + K + '">' + t('tap') + '</button><span class="tc-hint" id="mmTapV' + K + '" aria-live="polite"></span>';
            html += '</div>';
            if (acap) html += '<p class="tc-note">' + t(guess ? 'acap_guess_note' : 'acap_check_note') + '</p>';
        }
        box.innerHTML = html; box.hidden = false; slotIdle(k);
        var btn = box.querySelector('[data-bpm-apply]');
        if (btn) btn.addEventListener('click', function () {
            var v = parseFloat($('mmBpm' + K).value);
            // an a cappella's tempo panel reports as acap_tempo; a song whose grid failed keeps manual_bpm
            if (v >= 40 && v <= 240) { var from = st.tempoSrc; getEngine().setManualTempo(k, v, 'manual'); feature(acap ? 'acap_tempo' : 'manual_bpm', { k: k, a: 'manual', bpm: v, from: from }); afterTempoChange(k); }
        });
        Array.prototype.forEach.call(box.querySelectorAll('[data-bpm-scale]'), function (b) {
            b.addEventListener('click', function () { var f = parseFloat(b.dataset.bpmScale); getEngine().scaleTempo(k, f); feature('acap_tempo', { k: k, a: f > 1 ? 'x2' : 'half', bpm: getEngine().song(k).grid.bpm }); afterTempoChange(k); });
        });
        var chk = $('mmCheck' + K); if (chk) chk.addEventListener('click', function () { toggleCheck(k); });
        var tap = $('mmTap' + K); if (tap) tap.addEventListener('click', function () { onTap(k); });
    }
    function afterTempoChange(k) {
        var playing = check && check.k === k, pos = playing ? checkPos() : null;
        paintSlot(k); paintFit(); syncGo();
        if (built) resetWorkspace();                 // the finished mashup was built on the old grid
        if (playing) startCheck(k, pos);
    }

    // ── «does the tempo sit?» — the a cappella played with a click on its grid, and tap tempo ──
    // A voice has no drummer: the tempo from the file name/tags is trusted, a guess is not. The
    // person hears the click against the singing and fixes it by ×2/÷2, a number or tapping.
    var check = null, taps = [], tapTimer = null, tapBase = null;
    function firstVoiced(st) {
        var r = st.mix && st.mix.rms; if (!r || !r.data) return 0;
        var mx = 0, i; for (i = 0; i < r.data.length; i++) if (r.data[i] > mx) mx = r.data[i];
        for (i = 0; i < r.data.length; i++) if (r.data[i] > mx * 0.15) return Math.max(0, i * r.frameSec - 0.5);
        return 0;
    }
    function checkPos() { return check ? check.from + (check.ctx.currentTime - check.t0) : 0; }
    function stopCheck() {
        if (!check) return;
        try { check.src.stop(); } catch (_) {}
        (check.clicks || []).forEach(function (o) { try { o.stop(); } catch (_) {} });
        var b = $('mmCheck' + check.k.toUpperCase()); if (b) { b.textContent = t('tempo_check'); b.setAttribute('aria-pressed', 'false'); }
        check = null;
    }
    async function startCheck(k, from) {
        stopCheck();
        var st = getEngine().song(k); if (!st || !st.grid || !st.grid.beats.length) return;
        playbackSession();
        var c = audioCtx();
        try { if (window.__liveAudioCtxReady) { var c2 = await window.__liveAudioCtxReady(c); if (c2 && c2 !== ctx) { ctx = c2; getEngine().setContext(c2); if (mixer) mixer.setContext(c2); c = c2; } } } catch (_) { return; }
        if (mixer && mixer.isPlaying()) { mixer.pause(); setPlayUi(false); }
        // a phone let go of the song's sound after the server's stems: decoded again from the file
        var sound = st.buffer;
        if (!sound) { try { sound = await getEngine().ensureBuffer(k); } catch (_) { sound = null; } }
        if (!sound || getEngine().song(k) !== st) return;
        from = from != null ? from : firstVoiced(st);
        var LEN = 20, t0 = c.currentTime + 0.08, src = c.createBufferSource();
        src.buffer = sound; src.connect(c.destination); src.start(t0, Math.min(from, Math.max(0, st.duration - 1)), LEN);
        var g = st.grid, down = {}, clicks = [];
        for (var d = 0; d < (g.downIdx || []).length; d++) down[g.downIdx[d]] = 1;
        for (var i = 0; i < g.beats.length; i++) {
            var bt = g.beats[i]; if (bt < from || bt > from + LEN) continue;
            var o = c.createOscillator(), gn = c.createGain(), at = t0 + (bt - from);
            o.frequency.value = down[i] ? 1760 : 1175;
            gn.gain.setValueAtTime(0.0001, at); gn.gain.exponentialRampToValueAtTime(0.35, at + 0.002); gn.gain.exponentialRampToValueAtTime(0.0001, at + 0.05);
            o.connect(gn); gn.connect(c.destination); o.start(at); o.stop(at + 0.06); clicks.push(o);
        }
        check = { k: k, src: src, clicks: clicks, ctx: c, t0: t0, from: from };
        src.onended = function () { if (check && check.src === src) stopCheck(); };
        var b = $('mmCheck' + k.toUpperCase()); if (b) { b.textContent = t('tempo_check_stop'); b.setAttribute('aria-pressed', 'true'); }
        feature('acap_tempo', { k: k, a: 'check', bpm: g.bpm, src: st.tempoSrc });
    }
    function toggleCheck(k) { if (check && check.k === k) stopCheck(); else startCheck(k); }
    function onTap(k) {
        var st = getEngine().song(k); if (!st) return;
        // a tap during the check is placed on the FILE's time line (the beat the person heard);
        // without the check the taps still give a tempo, the phase then comes from the comb
        var onCheck = !!(check && check.k === k), now = onCheck ? checkPos() : performance.now() / 1000;
        // taps on two different clocks (before and during a check, or two checks) never mix
        var base = onCheck ? 'file:' + check.t0 : 'wall';
        if (base !== tapBase) { taps = []; tapBase = base; }
        if (taps.length && now - taps[taps.length - 1] > 2.5) taps = [];
        taps.push(now);
        var r = window.MashupGrid && window.MashupGrid.tapTempo(taps);
        var out = $('mmTapV' + k.toUpperCase());
        if (out) out.textContent = r ? t('tap_bpm', { bpm: Math.round(r.bpm * 10) / 10 }) : t('tap_more', { n: Math.max(0, 4 - taps.length) });
        clearTimeout(tapTimer);
        if (r) tapTimer = setTimeout(function () {
            var onFile = !!(check && check.k === k);
            getEngine().setManualTempo(k, r.bpm, 'tap', onFile ? r.anchor : null);
            feature('acap_tempo', { k: k, a: 'tap', bpm: r.bpm, n: taps.length, on_file: onFile ? 1 : 0 });
            taps = [];
            afterTempoChange(k);
        }, 1800);
    }
    function readyGrid(st) { return !!(st && st.grid && st.grid.beats.length); }
    /** The verdict of a singer over the music — the SAME rule the pair finder ranks by (MashupPlan.pairFit). */
    function fitOf(X, B) { return window.MashupPlan.pairFit({ bpm: X.grid.bpm, meter: X.grid.meter, key: X.mix && X.mix.key }, { bpm: B.grid.bpm, meter: B.grid.meter, key: B.mix && B.mix.key }); }
    /** The fact lines of one singer over the music; `tag` prefixes them when there are two singers. */
    function fitLines(X, B, f, tag) {
        var lines = [], pre = tag ? '<span class="mm-tag sm ' + tag.toLowerCase() + '" aria-hidden="true">' + tag + '</span> ' + (tag === 'C' ? t('fit_c') + ' ' : '') : '';
        var dir = f.stretch > 1 ? t('fit_dir_slower') : t('fit_dir_faster');
        // the vocals take the music's tempo: stretch > 1 means they play slower
        var tl = t('fit_tempo', { a: Math.round(X.grid.bpm), b: Math.round(B.grid.bpm) }) + ' — ' + (f.pct < 0.015 ? t('fit_tempo_same') : t('fit_tempo_pct', { pct: fmtPct(f.pct), dir: dir }));
        if (f.k !== 1) tl += '; ' + t('fit_tempo_octave');
        // the warning from 12 %: a vocal stretched past 6–8 % is audible and 12 % is where tools that care
        // stop (lacuna.fm refuses beyond it; guides give 8–10 %) — it was 20 %, twice that
        var far = f.pct > 0.12;
        lines.push('<li class="' + (far ? 'warn' : 'good') + '">' + pre + tl + (far ? '<span class="tc-hint">' + t('fit_tempo_far') + '</span>' : '') + '</li>');
        var ka = X.mix && X.mix.key, kb = B.mix && B.mix.key;
        if (ka && kb) lines.push('<li class="' + (f.rel === 'far' ? 'warn' : 'good') + '">' + pre + t('fit_key', { a: ka.name + ' (' + ka.camelot + ')', b: kb.name + ' (' + kb.camelot + ')' }) + ' — ' + t('fit_key_' + f.rel) + (tag === 'C' ? '' : '<span class="tc-hint">' + t('fit_key_after') + '</span>') + '</li>');
        if (f.meterOff) lines.push('<li class="warn">' + pre + t('fit_meter', { a: X.grid.meter + '/4', b: B.grid.meter + '/4' }) + '</li>');
        return lines;
    }
    function paintFit() {
        var E = getEngine(), A = E.song('a'), B = E.song('b'), C = cOn ? E.song('c') : null, box = $('mmFit');
        if (!readyGrid(A) || !readyGrid(B)) { box.hidden = true; return; }
        var f = fitOf(A, B), cf = readyGrid(C) ? fitOf(C, B) : null;
        var lines = fitLines(A, B, f, cf ? 'A' : null);
        if (keys.a && keys.a === keys.b) lines.push('<li class="warn">' + t('fit_same_file') + '</li>');
        if (cf) lines = lines.concat(fitLines(C, B, cf, 'C'));
        // the verdict first, from the same facts the lines state (MashupPlan.pairFit); with two singers
        // it is the harder of the two — the mashup is as good as its weakest fit
        var RANK = { good: 0, ok: 1, hard: 2 }, verdict = cf && RANK[cf.verdict] > RANK[f.verdict] ? cf.verdict : f.verdict;
        var vEl = $('mmFitV'); if (vEl) { vEl.className = 'mm-verdict ' + verdict; vEl.textContent = t('fit_' + verdict); }
        box.querySelector('ul').innerHTML = lines.join('');
        box.hidden = false;
        // what pairs people bring: the verdict once per pair (tempo gap, key relation, octave)
        var pairKey = [keys.a, keys.b, A.grid.bpm, B.grid.bpm].join('~');
        if (pairKey !== fitShownKey) {
            fitShownKey = pairKey;
            feature('fit_shown', { tpct: Math.round(f.pct * 1000) / 10, k: f.k, rel: f.rel, meter: (A.grid.meter || 4) + '/' + (B.grid.meter || 4), verdict: f.verdict,
                stem_a: A.isStem ? 1 : 0, stem_b: B.isStem ? 1 : 0, src_a: A.tempoSrc || 'model', same: keys.a === keys.b ? 1 : 0, a_sec: Math.round(A.duration), b_sec: Math.round(B.duration) });
        }
        var cKey = cf ? [keys.c, keys.b, C.grid.bpm].join('~') : null;
        if (cf && cKey !== fitShownKeyC) {
            fitShownKeyC = cKey;
            feature('fit_c', { tpct: Math.round(cf.pct * 1000) / 10, k: cf.k, rel: cf.rel, verdict: cf.verdict, stem_c: C.isStem ? 1 : 0, c_sec: Math.round(C.duration) });
        }
    }
    var fitShownKey = null, fitShownKeyC = null;
    /** Deck C holds a file that is still being read (or failed): the mashup waits for it or for «Remove». */
    function cPending() { return cOn && !!slotFile('c') && !ready.c; }
    /** Something in the build still has to be separated — songs whose stems are on the page already
     *  (a rebuild after a tempo fix, a third song added) need no graphics card and no server. */
    function needSeparation() { return !!(engine && engine.needsSeparation()); }
    function syncGo() {
        var E = engine, ok = !!(E && E.song('a') && E.song('b') && ready.a && ready.b);
        ok = ok && readyGrid(E.song('a')) && readyGrid(E.song('b')) && !cPending();
        var needSep = needSeparation();
        $('mmGo').disabled = !ok || !!running || (needSep && !!off && !cpuChosen);
        paintOff(needSep); paintSepEta();
        if (ok && needSep && off && !cpuChosen) {
            // both songs in, and the wall is the device: counted once per pair so the numbers say
            // how many people come this far and stop here (engine_support alone counts visits)
            var bk = keys.a + '~' + keys.b + '~' + (keys.c || '');
            if (bk !== blockedKey) { blockedKey = bk; feature('blocked', { why: off, cpu_offer: cpuOffer() ? 1 : 0, srv: SRV ? 1 : 0 }); }
        }
        paintSrv();
    }
    var blockedKey = null, cpuChosen = false, cpuEtaSec = 0;
    /** Minutes of CPU separation for the two songs, from THIS computer's known-answer segment:
     *  MUSION steps 5.6 s of audio per 7.8-s segment (45 s → 8 segments, measured). */
    function cpuEta(segMs) {
        var E = engine, n = 0;
        ['a', 'b', 'c'].forEach(function (k) {
            var s = E && E.song(k), want = E ? E.wantStems(k) : [];
            if (!s || s.isStem || (s.stems && want.every(function (w) { return !!s.stems[w]; }))) return;
            n += Math.ceil(s.duration / 5.6) + 1;
        });
        return n && segMs > 0 ? n * segMs / 1000 : 0;
    }
    var WHY = { no_webgpu: 'why_no_webgpu', software_gpu: 'why_no_webgpu', no_worker: 'why_no_webgpu', not_wired: 'why_no_webgpu', webkit: 'why_webkit', low_memory: 'why_low_memory', gpu_rejected: 'why_gpu_rejected', gpu_output_corrupt: 'why_gpu_rejected', ep_ladder_exhausted: 'why_gpu_rejected' };
    /**
     * A COMPUTER without WebGPU can still separate — on the processor, 10–20× slower (measured
     * 2026-09-24: 40–66 s per 7.8-s segment on a loaded desktop, ≈ 22 s on a quiet one; two 3.5-min
     * songs ≈ half an hour). Offered as a choice with that price, never started on its own; not on
     * phones (the model alone takes 1.1–1.4 GB of the tab) and not on WebKit (never measured there).
     */
    function cpuOffer() {
        if (!off || !/^(no_webgpu|software_gpu|gpu_rejected|gpu_output_corrupt|ep_ladder_exhausted)$/.test(off)) return false;
        var dc = null; try { dc = window.__deviceCap && window.__deviceCap.get ? window.__deviceCap.get() : null; } catch (_) {}
        if (dc && (dc.isMobile || dc.isWebKit || dc.isIOS || dc.isHandheld)) return false;
        if (/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent || '')) return false;
        var dm = navigator.deviceMemory;
        return !(typeof dm === 'number' && dm < 8);
    }
    function paintOff(needSep) {
        var show = !!off && needSep;
        $('mmOff').hidden = !show;
        if (!show) return;
        var offer = cpuOffer();
        $('mmOffText').textContent = cpuChosen ? (cpuEtaSec ? t('cpu_chosen_eta', { t: fmtEta(cpuEtaSec) }) : t('cpu_chosen'))
            : t('unavailable', { why: t(WHY[off] || 'why_no_webgpu') }) + (offer ? ' ' + t('cpu_note') : '');
        $('mmCpu').hidden = !offer || cpuChosen;
        $('mmOffStems').hidden = cpuChosen;
    }

    function fileKeyOf(f) { return f ? [f.name, f.size, f.lastModified].join('|') : null; }
    /** What a file's tags say — BPM for a DJ-pool file's grid (ID3 TBPM, Vorbis BPM), title and artist
     *  for the Reels card's words: read in ≤ 2 s or not at all. */
    var TAG_TITLE = { TIT2: 1, TT2: 1, TITLE: 1, '©NAM': 1, INAM: 1 }, TAG_ARTIST = { TPE1: 1, TP1: 1, ARTIST: 1, '©ART': 1, IART: 1, AUTHOR: 1 };
    function tagText(fields, ids) {
        for (var i = 0; i < (fields || []).length; i++) {
            var f = fields[i], id = String(f.id || f.name || '').toUpperCase();
            if (ids[id] && f.value && String(f.value).trim()) return String(f.value).trim().slice(0, 80);
        }
        return null;
    }
    var tagCache = {};
    function tagsOf(file) {
        var R = window.AudioTagReader, E0 = window.MashupEngine;
        if (!file || !R || !R.read) return Promise.resolve(null);
        return Promise.race([
            R.read(file).then(function (r) {
                var f = r && r.fields;
                return { bpm: E0 && E0.bpmFromTags ? E0.bpmFromTags(f) : null, title: tagText(f, TAG_TITLE), artist: tagText(f, TAG_ARTIST) };
            }, function () { return null; }),
            new Promise(function (res) { setTimeout(function () { res(null); }, 2000); })
        ]);
    }
    /** The file IN the slot now — the zone records it at delivery, before decoding (files[k] only
     *  after it): a swap or a decode during analysis must go by what the person put there. */
    function slotFile(k) { var z = $('mmUp' + k.toUpperCase()), d = z && z.__deliveredFiles; return (d && d[0]) || files[k] || null; }
    async function onDecoded(k, detail) {
        var buf = detail && detail.buffer, file = detail && detail.file;
        if (!buf) return;
        if (k === 'c' && !cOn) return;                              // deck C was closed while its file decoded
        // a decode that finishes after another file was put in this slot (a swap during analysis,
        // a quick re-pick) must not overwrite it — it used to leave the same song in both slots
        var cur = slotFile(k);
        if (cur && file && cur !== file) { feature('stale_decode', { k: k }); return; }
        if (check && check.k === k) stopCheck();
        files[k] = file; keys[k] = fileKeyOf(file); ready[k] = false;
        resetWorkspace();
        if (buf.duration > 12 * 60) {
            // said IN the slot: the toast alone vanished and left a loaded file with a grey button
            slotMsg(k, t('err_too_long'));
            feature('too_long', { k: k, sec: Math.round(buf.duration) });
            syncGo(); return;
        }
        setSlotBusy(k, true); syncGo();
        stage('upload_' + k, { sec: Math.round(buf.duration) });
        try {
            var tags = await tagsOf(file), tagBpm = tags && tags.bpm;
            if (files[k] !== file) return;
            tagCache[keys[k]] = tags;
            // the decoded sound goes to the engine and is NOT held here: this function waits for the
            // whole listening, and its reference was a second copy of every song on the page for
            // minutes (bug report 2026-09-28 — the tab of a 4 GB phone died six times, oom)
            var songP = getEngine().setSong(k, { buffer: buf, file: file, fileKey: keys[k], name: (file && file.name) || 'song', isStem: $('mmStem' + k.toUpperCase()).checked, tagBpm: tagBpm,
                onProgress: function (d, n) { if (files[k] === file) onSlotProgress(k, d, n); } });
            buf = null; detail = null;
            await songP;
            if (!getEngine().song(k) || getEngine().song(k).file !== file) return;
            var st = getEngine().song(k);
            if (isSilent(st)) { paintSlot(k); paintFit(); feature('silent', { k: k }); syncGo(); return; }
            ready[k] = true; paintSlot(k); paintFit();
            prewarmSeparator();
            if (k === 'b') musicLufs().catch(function () {});
            var g = st.grid, mg = st.modelGrid || {};
            // w — workers that shared the song, ch/cms — its model chunks and their average, first — ms to
            // the first chunk (the queue it waited in), att — it joined an analysis already running
            feature('song_analyzed', { k: k, bpm: g.bpm, conf: g.confidence, bars: g.downIdx.length, meter: g.meter, ms: st.analysisMs, cached: st.cached ? 1 : 0,
                stem: st.isStem ? 1 : 0, src: st.tempoSrc || 'model', tag: tagBpm || null, name_bpm: st.bpmHint || null,
                p: (window.MashupAnalysis.info() || {}).provider, m: (window.MashupAnalysis.info() || {}).model,
                w: mg.workers || null, ch: mg.chunks || null, cms: mg.chunkMs || null, mms: mg.modelMs || null, first: mg.firstMs != null ? mg.firstMs : null, att: st.attached ? 1 : 0,
                sec: Math.round(st.duration), hid: document.visibilityState === 'hidden' ? 1 : 0 });
            // an a cappella never gets this toast: its panel says how sure the tempo is and how to check it
            if (!(st.isStem && k !== 'b') && (!g.beats.length || g.confidence < 0.35)) window.__toast(t('err_no_beat', { name: st.name }), 'warning');
        } catch (e) {
            slotEl(k, 'Stats').hidden = true; slotIdle(k);
            // the song left the slot (another file, removed): its analysis was dropped on purpose
            if (e && e.name === 'AbortError' && files[k] !== file) { syncGo(); return; }
            try { console.error('[mashup analyze]', e); } catch (_) {}
            try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'analyze_failed', k: k, code: e && (e.code || (e.name === 'AbortError' ? 'aborted' : null)) || null, err: String(e && e.message || e).slice(0, 120) }); } catch (_) {}
            window.__handleError(new Error(/model_fetch/.test(String(e && e.code || e && e.message)) ? t('err_model_net') : t('err_failed')), SLUG);
        }
        syncGo();
    }
    /** Both songs are in: fetch and verify the separation model while the person reads the verdict
     *  (84 MB once, then the browser cache). Nothing is separated before the press. */
    var prewarmed = false;
    function prewarmSeparator() {
        if (prewarmed || off || !ready.a || !ready.b) return;
        var E = getEngine();
        if (!E.needsSeparation()) return;                            // everything is a stem already, or on the page
        var M = window.MusionStemSeparator;
        if (!M || !M.preload) return;
        prewarmed = true;
        M.preload(SLUG, { onProgress: function () {} }).then(function (info) { feature('sep_prewarm', { ep: info && info.provider, seg_ms: info && info.segMs }); sepInfo = info || null; paintSepEta(); }, function () { prewarmed = false; });
    }
    /** The separation's time on this graphics card, from its own known-answer segment — said before the press
     *  when it is minutes (a phone GPU: 26–40 s a segment, half an hour for two songs). */
    var sepInfo = null;
    function paintSepEta() {
        var el = $('mmSepEta'); if (!el) return;
        var gpu = sepInfo && sepInfo.provider && sepInfo.provider !== 'wasm' && sepInfo.segMs > 0 && !off;
        var sec = gpu && !built && needSeparation() ? cpuEta(sepInfo.segMs) : 0;
        el.hidden = !(sec >= 180);
        if (!el.hidden) el.textContent = t('sep_eta', { t: fmtEta(sec) }) + (isMobile() ? ' ' + t('keep_open') : '');
    }
    function onClear(k) {
        if (check && check.k === k) stopCheck();
        files[k] = null; keys[k] = null; ready[k] = false; slotEl(k, 'Stats').hidden = true; slotIdle(k);
        if (deckEl(k)) deckEl(k).classList.remove('is-loaded');
        // the song's analysis, if it is still running, yields to the others and is dropped unless the
        // same file comes back (MashupEngine: parked, not thrown away — a re-pick picks it up)
        if (engine) engine.clear(k);
        // the second vocal leaves a BUILT mashup without a rebuild: the studio goes on with A alone
        if (k === 'c' && built) { setWhich('a'); pushTracks(); paintTimeline(); paintKey(); paintTracks(); paintRegion(); paintWhich(); paintFit(); syncGo(); return; }
        $('mmFit').hidden = true; paintFit(); resetWorkspace(); syncGo();
    }

    ['a', 'b', 'c'].forEach(function (k) {
        var zone = $('mmUp' + k.toUpperCase());
        if (!zone) return;
        var onPick = function (file, detail) {
            ready[k] = false;
            // the same file is already on the page (B's song again as C's second singer — the report
            // of 2026-09-28 did exactly that): the zone takes its sound from the engine instead of
            // decoding a second copy of it (tool-uploader.js reuseAudio; the engine keeps one per file)
            try { var have = file && engine && engine.bufferFor(fileKeyOf(file)); if (have && detail && detail.reuseAudio) detail.reuseAudio(have); } catch (_) {}
            // the slot's old stems go now, not after the decode: a phone holds one song's stems less
            if (engine) engine.dropStems(k);
            // the file on its way in: its analysis, if one is running (the same song picked again), is
            // taken up as it is, and the song it replaces stops holding the workers
            try { if (file) getEngine().expect(k, fileKeyOf(file)); } catch (_) {}
            syncGo();
            // a loaded deck drops its «what goes here» line: the file row now says it
            if (deckEl(k)) deckEl(k).classList.add('is-loaded');
            // the beat model's cold start (download + session + GPU check ≈ 10–30 s) overlaps the
            // decode of the file the person just picked, instead of starting after it
            try { window.MashupAnalysis && window.MashupAnalysis.ensure().catch(function () {}); } catch (_) {}
        };
        if (typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(zone, onPick); else zone.addEventListener('tool-uploader:file', onPick);
        zone.addEventListener('tool-uploader:decoded', function (e) { onDecoded(k, e.detail); });
        zone.addEventListener('tool-uploader:clear', function () { onClear(k); });
        $('mmStem' + k.toUpperCase()).addEventListener('change', function () {
            var st = getEngine().song(k);
            feature('is_stem', { k: k, on: this.checked ? 1 : 0 });
            if (check && check.k === k) stopCheck();
            // the engine owns which grid an a cappella gets (name / tags / guess) — and gives the
            // song's own grid back when the box is unticked
            if (st) { getEngine().setIsStem(k, this.checked); if (st.grid) { paintSlot(k); paintFit(); } }
            resetWorkspace(); syncGo();
        });
    });
    // ── deck C: a second singer over the same music ─────────────────────────
    function openC(on, why) {
        cOn = !!on;
        var d = $('mmDeckC'); if (d) d.hidden = !cOn;
        $('mmAddC').hidden = cOn;
        if (cOn) {
            feature('c_open', { why: why || 'button', built: built ? 1 : 0 });
            // the deck's own «choose file» button, without moving the page
            var b = d && d.querySelector('.tu-cta, button, input[type=file]');
            if (b && why !== 'restore') { try { b.focus({ preventScroll: true }); } catch (_) {} }
        } else {
            if (check && check.k === 'c') stopCheck();
            if (window.ToolUploader && window.ToolUploader.clear && slotFile('c')) window.ToolUploader.clear('mmUpC');
            else onClear('c');
            $('mmStemC').checked = false;
            try { $('mmAddC').focus({ preventScroll: true }); } catch (_) {}
        }
        paintFit(); syncGo();
    }
    $('mmAddC').addEventListener('click', function () { openC(true); });
    $('mmDropC').addEventListener('click', function () { feature('c_drop', { built: built ? 1 : 0, had: slotFile('c') ? 1 : 0 }); openC(false); });

    $('mmSwap').addEventListener('click', function () {
        var fa = slotFile('a'), fb = slotFile('b');
        feature('swap', { both: fa && fb ? 1 : 0 });
        var ca = $('mmStemA').checked; $('mmStemA').checked = $('mmStemB').checked; $('mmStemB').checked = ca;
        if (!window.ToolUploader || !window.ToolUploader.inject) return;
        if (fb) window.ToolUploader.inject('mmUpA', fb, 'swap'); else window.ToolUploader.clear && window.ToolUploader.clear('mmUpA');
        if (fa) window.ToolUploader.inject('mmUpB', fa, 'swap'); else window.ToolUploader.clear && window.ToolUploader.clear('mmUpB');
    });

    // ── the engine is closed on this device? (said before anyone waits) ─────
    function probeSupport(tries) {
        var M = window.MusionStemSeparator;
        if (!M || !M.support) {
            if ((tries || 0) < 4) { setTimeout(function () { probeSupport((tries || 0) + 1); }, 1500); return; }
            off = 'not_wired'; syncGo(); return;
        }
        M.support().then(function (s) {
            off = s && s.ok ? null : ((s && s.reason) || 'no_webgpu');
            feature('engine_support', { ok: off ? 0 : 1, why: off || null, gpu: s && s.adapter ? [s.adapter.vendor, s.adapter.architecture].join('/') : null });
            syncGo();
        }).catch(function () { off = 'no_webgpu'; syncGo(); });
    }

    // ── the separation on OUR server (the site's stem-split lane) ─────────────
    // Where the device cannot separate two songs — a phone (no WebGPU for this model, a tab that
    // dies at 500–700 MB), a computer without a graphics card (half an hour on the processor) —
    // the same Demucs jobs /stem-splitter sells: its per-minute price, its daily allowance, a
    // Premium device without a graphics card inside the plan, the stems kept in «Мои генерации».
    // Said before the press: nothing leaves the page without one.
    var SRV = CFG.srv || null, srvLane = null, srvMod = null, srvInfo = null, srvInfoAt = 0, srvLoading = null, srvFailAt = 0;
    var srvOffer = null, srvShownKey = null, srvCache = {};
    function isMobile() {
        var dc = null; try { dc = window.__deviceCap && window.__deviceCap.get ? window.__deviceCap.get() : null; } catch (_) {}
        // isHandheld: a phone in «desktop site» mode says Linux x86_64 (bug report 2026-09-28)
        return !!(dc && (dc.isMobile || dc.isIOS || dc.isHandheld)) || /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent || '');
    }
    function srvApi() {
        if (!SRV || !window.StemSplitLane || !window.MashupServer) return null;
        if (!srvMod) {
            srvLane = window.StemSplitLane.create({ infoUrl: SRV.infoUrl, runUrl: SRV.runUrl, mode: SRV.mode });
            srvMod = window.MashupServer.create({ lane: srvLane, ctx: audioCtx, maxMb: SRV.maxMb });
        }
        return srvMod;
    }
    function fmtTok(n) { try { return new Intl.NumberFormat(window.__LOCALE || 'en', { numberingSystem: 'latn' }).format(n); } catch (_) { return String(n); } }
    /** What the look at the lane asks about: the pair's songs as they would go up (name, bytes) — the
     *  lane answers which of them this person already had split, so no price is named for stems
     *  that are already theirs (a rebuild after a reload used to read «3 125 tokens» and cost 0). */
    function srvAsk() {
        var S = srvApi(), E = engine;
        if (!S || !E || !E.song('a') || !E.song('b')) return { files: [], key: '' };
        var ids = S.identify(S.needs(E));
        return { files: ids, key: ids.map(function (i) { return i.k + '|' + i.n + '|' + i.b; }).join('~') };
    }
    function srvRefresh(force) {
        if (!srvApi()) return Promise.resolve(null);
        var ask = srvAsk();
        if (!force && srvInfo && srvInfo.__ask === ask.key && Date.now() - srvInfoAt < 60000) return Promise.resolve(srvInfo);
        if (srvLoading && srvLoading.key === ask.key) return srvLoading.p;
        var p = srvLane.info({ files: ask.files }).then(function (d) {
            if (srvLoading && srvLoading.p === p) srvLoading = null;
            if (d) d.__ask = ask.key;
            srvInfo = d; srvInfoAt = Date.now(); paintSrv(); return d;
        }, function () { if (srvLoading && srvLoading.p === p) srvLoading = null; srvFailAt = Date.now(); return null; });
        srvLoading = { key: ask.key, p: p };
        return p;
    }
    function paintSrv() {
        var box = $('mmSrv'), E = engine, go = $('mmSrvGo'), note = $('mmSrvNote');
        var ok = !!(E && E.song('a') && E.song('b') && ready.a && ready.b) && !cPending();
        var needSep = needSeparation();
        var mob = isMobile();
        // a finished mashup needs no separation offer under it (it read «N tokens» for stems already on the page)
        if (!ok || built || !needSep || !(off || mob) || !srvApi()) { box.hidden = true; srvOffer = null; return; }
        // the answer must be about THIS pair (which of its songs are already yours); a failed look is not
        // asked again on every repaint — sliders repaint this dozens of times a second
        if (!srvInfo || srvInfo.__ask !== srvAsk().key) { box.hidden = true; if (Date.now() - srvFailAt > 10000) srvRefresh(); return; }
        var S = srvApi(), reason = S.reasonFor(off, mob), nds = S.needs(E);
        // stems this visit already brought down (a rebuild after a tempo fix) cost nothing and upload nothing
        var fresh = nds.filter(function (nd) { return !srvCached(srvKey(nd)); });
        var of = srvOffer = fresh.length ? S.offer(srvInfo, fresh, reason) : { state: 'cached', n: nds.length, free: 0, cost: 0 };
        if (of.state === 'off' || of.state === 'none') { box.hidden = true; return; }
        // a phone the engine COULD run on still hears why the server is the safer road
        var lead = off ? '' : t('srv_mobile') + ' ', text = '', btn = null;
        // one song of the pair already yours: said first, and the price below is for the other one only
        var pre = of.owned > 0 && of.state !== 'owned' ? t('srv_owned_one') + ' ' : '';
        if (of.state === 'cached') { text = t('srv_cached'); btn = t('srv_go_again'); }
        else if (of.state === 'owned') { text = t(of.live ? 'srv_owned_live' : 'srv_owned'); btn = t('btn_go'); }
        else if (of.state === 'login') { text = lead + t('srv_login_note', { n: fmtTok(of.cost) }); btn = t('srv_login'); }
        else if (of.state === 'too_long') text = pre + t('srv_too_long', { min: Math.round(of.max / 60) });
        else if (of.state === 'insufficient') { text = pre + t('srv_insufficient', { n: fmtTok(of.cost), b: fmtTok(of.balance) }); btn = of.store ? t('srv_topup') : null; }
        else if (of.free === of.n - (of.owned || 0)) { text = lead + pre + t('srv_free', { left: of.fleft, cap: of.fcap }); btn = t('srv_go'); }
        else if (of.free > 0) { text = lead + pre + t('srv_free_part', { n: fmtTok(of.cost) }); btn = t('srv_go_upto', { n: fmtTok(of.cost) }); }
        else { text = lead + pre + t('srv_price', { n: fmtTok(of.cost), b: fmtTok(of.balance) }); btn = t('srv_go_price', { n: fmtTok(of.cost) }); }
        $('mmSrvText').textContent = text;
        go.hidden = !btn; if (btn) go.textContent = btn;
        go.disabled = !!running;
        note.hidden = of.state !== 'ready';
        if (of.state === 'ready') note.textContent = t('srv_note');
        box.hidden = false;
        // how far people get on this road, once per pair and state
        var sk = [keys.a, keys.b, keys.c || '', of.state].join('~');
        if (sk !== srvShownKey) {
            srvShownKey = sk;
            feature('srv_offer', { state: of.state, n: of.n, owned: of.owned || 0, free: of.free || 0, cost: of.cost || 0, bal: of.balance != null ? of.balance : null, left: of.left, why: off || 'mobile', reason: reason });
        }
    }
    // login / top-up leave the page: the two files ride the site's hand-off (IndexedDB, single use,
    // 30 min) and come back into their slots, with the «already an a cappella» boxes as they were
    var CARRY = { a: 'mm_srv_carry_a', b: 'mm_srv_carry_b', c: 'mm_srv_carry_c', flags: 'mm_srv_carry' };
    function srvCarry(url) {
        var left = false, go = function () { if (left) return; left = true; window.location.href = url; };
        try { sessionStorage.setItem(CARRY.flags, JSON.stringify({ a: $('mmStemA').checked, b: $('mmStemB').checked, c: $('mmStemC').checked, cOn: cOn ? 1 : 0, at: Date.now() })); } catch (_) {}
        var puts = [];
        ['a', 'b', 'c'].forEach(function (k) { var f = (k !== 'c' || cOn) ? slotFile(k) : null; if (f && window.__toolHandoffPut) puts.push(window.__toolHandoffPut(CARRY[k], f)); });
        Promise.all(puts).then(go, go);
        setTimeout(go, 2500);                                       // a slow disk never holds the door
    }
    function srvRestore() {
        var flags = null;
        try { flags = JSON.parse(sessionStorage.getItem(CARRY.flags) || 'null'); sessionStorage.removeItem(CARRY.flags); } catch (_) {}
        if (!flags || Date.now() - (flags.at || 0) > 30 * 60000 || typeof window.__toolHandoffTake !== 'function') return;
        if (flags.cOn) openC(true, 'restore');
        ['a', 'b', 'c'].forEach(function (k) {
            if (k === 'c' && !flags.cOn) return;
            window.__toolHandoffTake(CARRY[k]).then(function (f) {
                if (!f) return;
                var tries = 0;
                (function put() {
                    if (slotFile(k)) return;
                    if (window.ToolUploader && window.ToolUploader.inject) {
                        $('mmStem' + k.toUpperCase()).checked = !!flags[k];
                        window.ToolUploader.inject('mmUp' + k.toUpperCase(), f, 'login_return');
                        feature('srv_carry_back', { k: k });
                        return;
                    }
                    if (++tries < 40) setTimeout(put, 100);
                })();
            }, function () {});
        });
    }
    $('mmSrvGo').addEventListener('click', function () {
        var of = srvOffer, btn = this;
        if (!of || running) return;
        if (of.state === 'login') { feature('srv_login', { n: of.n }); srvCarry(SRV.loginUrl); return; }
        if (of.state === 'insufficient') { feature('srv_topup', { need: of.cost, bal: of.balance }); if (of.store) srvCarry(of.store); return; }
        // stems already on the page, or already this person's on the server: nothing to price or re-check —
        // an owned song goes up as «only if it is still mine», so a twin gone by now is refused, not bought
        if (of.state === 'cached' || of.state === 'owned') { if (check) stopCheck(); run({ server: of }); return; }
        if (of.state !== 'ready') return;
        // the price on the button came from an earlier look at the wallet: ask again, and start only
        // if the server's answer still says what the button said
        btn.disabled = true;
        srvRefresh(true).then(function () {
            btn.disabled = false;
            var now = srvOffer;
            if (!now || now.state !== 'ready' || now.cost > of.cost || now.free < of.free) { feature('srv_changed', { from: of.state, to: now && now.state }); return; }
            if (check) stopCheck();
            run({ server: now });
        });
    });

    /** One entry per distinct song; the stems of a song already fetched in this visit are reused
     *  (a rebuild after a tempo fix does not download them again). */
    function srvKey(nd) { return (nd.song.fileKey || 'slot:' + nd.ks[0]) + '|' + nd.want.join(','); }
    /** A cached download is a cache only while it still holds the stems: a device short of memory has the
     *  engine TAKE them (MashupEngine separate — kept here they stayed at float next to the engine's packed
     *  ones), and a rebuild that needs them again asks the server — the songs are owned, nothing is charged. */
    function srvCached(key) { var p = srvCache[key]; return !!p && !(p.__v && !p.__v.vocals); }
    function srvStart(E, nds, of, signal, onPhase) {
        var S = srvApi(), fresh = [], ext = {}, keyOf = srvKey;
        var live = {}; nds.forEach(function (nd) { live[keyOf(nd)] = 1; });
        Object.keys(srvCache).forEach(function (k) { if (!live[k]) delete srvCache[k]; });
        nds.forEach(function (nd) { if (!srvCached(keyOf(nd))) { delete srvCache[keyOf(nd)]; fresh.push(nd); } else nd.cached = true; });
        var started = fresh.length ? S.start(E, fresh, of, { signal: signal, onPhase: function (i, p, d) { onPhase(fresh[i], p, d); } }) : {};
        nds.forEach(function (nd) {
            var key = keyOf(nd), p = srvCache[key];
            if (!p) {
                p = srvCache[key] = started[nd.ks[0]];
                p.catch(function () { if (srvCache[key] === p) delete srvCache[key]; });
                p.then(function (v) { p.__v = v; }, function () {});
            } else onPhase(nd, 'done', { reused: 'page' });
            nd.ks.forEach(function (k) { ext[k] = p; });
        });
        return ext;
    }
    /** The progress of the server road: upload → waiting for a GPU → separating → the stems coming down. */
    function srvTracker(nds) {
        var st = nds.map(function () { return { p: 'upload', f: 0 }; });
        function paint() {
            var avg = st.reduce(function (a, s) { return a + s.f; }, 0) / st.length;
            var i = 0; st.forEach(function (s, j) { if (s.f < st[i].f) i = j; });
            var s = st[i], name = nds[i].song.name, now = Date.now(), label;
            if (s.p === 'upload') label = t('stage_srv_upload', { name: name, p: s.pct || 0 });
            else if (s.p === 'queued') { var q = (now - s.q0) / 1000; label = t('stage_srv_queue', { t: fmtTime(q) }) + (q > 60 ? ' ' + t('srv_cold') : ''); }
            else if (s.p === 'processing') label = t('stage_srv_run', { name: name, t: fmtTime((now - (s.r0 || s.q0)) / 1000) });
            else if (s.p === 'download') label = t('stage_srv_dl', { got: Math.round((s.got || 0) / 1048576), total: Math.max(1, Math.round((s.total || 0) / 1048576)) });
            else label = t('stage_melody');
            progress(0.02 + 0.8 * avg, label);
        }
        return {
            state: st,
            on: function (nd, phase, d) {
                var i = nds.indexOf(nd); if (i < 0) return;
                var s = st[i], now = Date.now();
                d = d || {};
                if (phase === 'upload') { s.p = 'upload'; s.pct = d.total ? Math.round(100 * d.loaded / d.total) : 0; s.f = 0.15 * (s.pct / 100); if (!s.u0) s.u0 = now; }
                else if (phase === 'submitted') { s.p = 'queued'; s.f = 0.15; s.q0 = now; s.job = d; }
                else if (phase === 'queued' || phase === 'processing') {
                    if (!s.q0) s.q0 = now;
                    if (phase === 'processing' && !s.r0) s.r0 = now;
                    s.p = phase;
                    // no percentage comes from a GPU queue — creep, never claim to be done
                    var el = (now - (s.r0 || s.q0)) / 1000;
                    s.f = 0.15 + 0.6 * (1 - Math.exp(-el / (phase === 'processing' ? 60 : 240)));
                }
                else if (phase === 'download') { s.p = 'download'; s.got = d.loaded; s.total = d.total; s.f = 0.75 + 0.25 * (d.total ? d.loaded / d.total : 0); }
                else if (phase === 'done') { s.p = 'done'; s.f = 1; if (d && d.jobId) s.job = d; if (d && d.reused === 'page') s.page = true; }
                paint();
            }
        };
    }
    var SRV_ERR = {
        gpu_unavailable: 'err_srv_busy', capacity: 'err_srv_busy',
        split_failed: 'err_srv_failed', server_error: 'err_srv_failed', decode: 'err_srv_decode', gone: 'err_srv_failed', expired: 'err_srv_failed', download: 'err_srv_net',
        source_not_audio: 'err_srv_file', source_too_short: 'err_srv_file', invalid: 'err_srv_file', source_too_large: 'err_srv_file',
        timeout: 'err_srv_timeout', network: 'err_srv_net', session_expired: 'err_srv_session', file_lost: 'err_srv_file_lost'
    };

    // ── the run ────────────────────────────────────────────────────────────
    function progress(p, text) { $('mmProgress').classList.add('on'); $('mmBar').style.width = Math.round(Math.max(0, Math.min(1, p)) * 100) + '%'; if (text != null) $('mmStage').textContent = text; }
    function hideProgress() { $('mmProgress').classList.remove('on'); $('mmBar').style.width = '0%'; $('mmStage').textContent = ''; $('mmCancel').hidden = true; }

    /** @param ro  { server: offer } — separate on our server (mm-server.js) instead of this device */
    async function run(ro) {
        ro = ro || {};
        var srvRun = ro.server || null;
        var E = getEngine(), A = E.song('a'), B = E.song('b');
        if (!A || !B) return;
        var setKey = 'set:' + [keys.a || 'a', keys.b || 'b'].sort().join('~');
        // The server road is work paid for in tokens: a split bought now, stems bought earlier
        // (owned) or brought down from that split in this visit (cached); the free server slots
        // are Premium's, who has no wall to meet. Work paid with tokens has no personal daily
        // limit (Farid 30.09 / 09.10.2026: «Там где за токены там нет лимита вообще») — the
        // daily unit counts only the build made on this device.
        var unit = (window.__toolLimit && !srvRun) ? window.__toolLimit.begin(SLUG, setKey) : null;
        if (window.__toolLimit && !srvRun && !unit) return;
        // clear the previous result FIRST: resetWorkspace() bumps runGen and aborts `running`, so
        // called after they are set it cancelled this very run in its first millisecond (found by
        // the e2e test: the MUSION worker was never even created, the stage label froze forever)
        resetWorkspace();
        // the listening is over (the button waits for it): its workers — half a gigabyte each on WASM —
        // give their memory to the build; the pair finder, if it was reading songs, stops here
        try { if (pairF) pairF.stop(); } catch (_) {}
        try { window.MashupAnalysis && window.MashupAnalysis.release && window.MashupAnalysis.release(); } catch (_) {}
        var gen = ++runGen, ctl = new AbortController(), t0 = performance.now();
        running = ctl; syncGo(); mode('is-running', true);
        $('mmCancel').hidden = false;
        progress(0.02, t('stage_model'));
        stage('build', { a: Math.round(A.duration), b: Math.round(B.duration) });
        var C = E.song('c'), sepC = !!(C && !C.isStem && E.wantStems('c').some(function (w) { return !(C.stems && C.stems[w]); }));
        var segT = { }, W = sepC ? { model: [0, 0.06], sep_a: [0.06, 0.32], sep_b: [0.32, 0.58], sep_c: [0.58, 0.82], melody: [0.82, 0.88], plan: [0.88, 0.9], render: [0.9, 0.99] }
            : { model: [0, 0.08], sep_a: [0.08, 0.45], sep_b: [0.45, 0.82], sep_c: [0.45, 0.82], melody: [0.82, 0.88], plan: [0.88, 0.9], render: [0.9, 0.99] };
        // A worker that stops answering (a GPU reset mid-segment, an OS-frozen thread) leaves the
        // build promise pending forever behind a still progress bar — the QA run of 24.09 sat on
        // «29 of 39» for 108 minutes. Every stage call is a heartbeat; silence past the stage's
        // budget aborts the run, frees the separator's worker and says so. Budgets are far above
        // the measured work: a GPU segment 0.4–3 s, a CPU one 22–66 s, the render ≤ 30 s.
        // Silence is counted in VISIBLE time: a phone that freezes a background tab stops the
        // separator too, and wall-clock minutes spent in another app used to read as a hang the
        // moment the person came back (the watchdog would abort a build that was only paused).
        var vnow = typeof window.__visibleNow === 'function' ? window.__visibleNow : function () { return performance.now(); };
        var beat = { t: vnow(), s: 'model', wall: performance.now() }, stalled = null;
        var cpuRun = !!(cpuChosen && off) && !srvRun;
        // the server road beats on every status answer (3 s); its own ceiling is the lane's poll
        // budget, so the watchdog only catches a page that stopped hearing from the server at all
        // Silence is counted between the separator's signs of life — every run of its model (onBeat) and every
        // step of its download, not only a song segment done — and the patience is this device's own: three of
        // its measured segments (the known-answer test's `segMs`) or 150 s, whichever is longer; before it has
        // been measured, 300 s — one run can be that long before the first measure (field RU+intl 25.09–18.10:
        // a known-answer round up to 293 s, p95 15.6 s; a segment up to 85 s). A phone GPU runs a segment in
        // 26–40 s and spent 57–240 s on the known-answer rounds and the bass probes before the first segment
        // reported: 25 «stuck» separations on RU (28.09–18.10.2026), 16 never built — one desktop gave up five
        // times in a row and built on the sixth (40 s a segment).
        var segMsNow = function () { var i = window.MusionStemSeparator && window.MusionStemSeparator.info ? window.MusionStemSeparator.info() : null; return i && i.segMs > 0 ? i.segMs : 0; };
        var sepPatience = function () { var m = segMsNow(); return m ? Math.max(150000, 3 * m + 30000) : 300000; };
        var wd = setInterval(function () {
            if (running !== ctl) { clearInterval(wd); return; }
            var sep = beat.s === 'sep_a' || beat.s === 'sep_b' || beat.s === 'sep_c' || beat.s === 'model' || beat.s === 'srv';
            var limit = sep ? (cpuRun || srvRun ? 600000 : sepPatience()) : 600000;
            if (vnow() - beat.t > limit) {
                stalled = beat.s; clearInterval(wd);
                try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'build_stalled', stage: beat.s, silent_ms: Math.round(vnow() - beat.t), hidden_ms: Math.max(0, Math.round((performance.now() - beat.wall) - (vnow() - beat.t))), cpu: cpuRun ? 1 : 0, seg_ms: Math.round(segMsNow()) || null, beats: beat.n }); } catch (_) {}
                try { window.MusionStemSeparator && window.MusionStemSeparator.release && window.MusionStemSeparator.release(); } catch (_) {}
                try { ctl.abort(); } catch (_) {}
            }
        }, 5000);
        var ext = null, srvT = null, srvNds = null;
        try { await musicLufs(); } catch (_) {}
        if (srvRun) {
            srvNds = srvApi().needs(E);
            srvT = srvTracker(srvNds);
            ext = srvStart(E, srvNds, srvRun, ctl.signal, function (nd, p, d) {
                beat.t = vnow(); beat.wall = performance.now(); beat.s = 'srv';
                if (gen === runGen) srvT.on(nd, p, d);
            });
            feature('srv_start', { n: srvNds.length, owned: srvNds.filter(function (nd) { return nd.owned; }).length, free: srvRun.free || 0, cost: srvRun.cost || 0, why: off || 'mobile',
                cached: srvNds.filter(function (nd) { return nd.cached; }).length });
        }
        try {
            var res = await E.build({
                signal: ctl.signal, allowWasm: cpuRun, external: ext,
                onBeat: function () { beat.t = vnow(); beat.wall = performance.now(); beat.n = (beat.n || 0) + 1; },
                onStage: function (s, p, info) {
                    beat.t = vnow(); beat.wall = performance.now(); beat.s = s;
                    if (gen !== runGen) return;
                    // the server road paints its own progress until the stems are in
                    if (srvRun && (s === 'model' || s === 'sep_a' || s === 'sep_b' || s === 'sep_c')) { beat.s = 'srv'; return; }
                    var w = W[s] || [0, 1], label;
                    if (s === 'sep_a' || s === 'sep_b' || s === 'sep_c') {
                        var name = ({ sep_a: A, sep_b: B, sep_c: C }[s] || A).name;
                        if (info && info.of) {
                            if (!segT[s]) segT[s] = { t: performance.now(), d: info.done };
                            var el = segT[s], eta = info.done > el.d ? (performance.now() - el.t) / (info.done - el.d) * (info.of - info.done) / 1000 : 0;
                            // the songs still to come after this one (about as long each)
                            eta += eta * ((s === 'sep_a' ? 1 : 0) + (s !== 'sep_c' && sepC ? 1 : 0));
                            label = t('stage_' + s, { name: name, done: info.done, of: info.of }) + (eta > 30 ? ' · ' + t('eta', { t: fmtEta(eta) }) : '');
                            progress(w[0] + (w[1] - w[0]) * (info.done / info.of), label);
                        } else progress(w[0], t('stage_' + s, { name: name, done: 0, of: '…' }));
                    } else if (s === 'model') progress(W.model[0] + (W.model[1] - W.model[0]) * Math.min(1, p / 0.3), p >= 0.25 ? t('stage_check') : t('stage_model'));
                    else progress(w[0], t('stage_' + s));
                }
            });
            if (gen !== runGen) return;
            built = true;
            await showWorkspace();
            if (unit) { unit.commit(); unit = null; }
            var pl = res.plan, info = window.MashupAnalysis.info() || {}, mi = (window.MusionStemSeparator && window.MusionStemSeparator.info && window.MusionStemSeparator.info()) || {};
            try {
                window.__aeToolComplete && window.__aeToolComplete({ ms: Math.round(performance.now() - t0), sep_a: res.times.sepA, sep_b: res.times.sepB, feat: res.times.feat, render: res.times.render,
                    a_sec: Math.round(A.duration), b_sec: Math.round(B.duration), bpm_a: pl.bpmA, bpm_b: pl.bpmB, k: pl.k, stretch: Math.round(pl.stretch * 1000) / 1000,
                    shift: pl.shift, v: pl.vocalShift, i: pl.instShift, rap: pl.rap ? 1 : 0, bar: pl.o, entry: pl.bEntry, a0: pl.a0, beat_ep: info.provider || null, beat_m: info.model || null, sep_ep: mi.provider || null,
                    stem_a: A.isStem ? 1 : 0, stem_b: B.isStem ? 1 : 0, src_a: A.tempoSrc || 'model', cpu: cpuRun ? 1 : 0, srv: srvRun ? 1 : 0,
                    c: res.planC ? 1 : 0, c_sec: C && res.planC ? Math.round(C.duration) : null, sep_c: res.times.sepC || null,
                    bar_c: res.planC ? res.planC.o : null, shift_c: res.planC ? res.planC.shift : null, v_c: res.planC ? res.planC.vocalShift : null,
                    a_bars: pl.a1 - pl.a0, c_bars: res.planC ? res.planC.a1 - res.planC.a0 : null,
                    // what of the singer's track is heard, in its own seconds (against a_sec: the share kept), and
                    // where on the music it starts — the two numbers a «my vocal came in cut / in the wrong place» is read from
                    a_from: pl.extent ? Math.round(pl.extent.t0 * 10) / 10 : null, a_to: pl.extent ? Math.round(pl.extent.t1 * 10) / 10 : null,
                    land_s: pl.landingB ? Math.round(pl.landingB.t) : null, phr_a: A.feat && A.feat.phrases ? A.feat.phrases.length : null,
                    // the music going round under a longer vocal: its body in bars and how many times more
                    loop_n: E.arrangement() ? E.arrangement().n : 0, loop_bars: E.arrangement() ? E.arrangement().l1 - E.arrangement().l0 : null });
            } catch (_) {}
            if (srvRun) {
                // what the server road cost in time and bytes, per song — the numbers a «the server is
                // slow» complaint will be checked against (a cold GPU host pulls the image for minutes)
                try {
                    srvT.state.forEach(function (s, i) {
                        var j = s.job || {};
                        feature('srv_song', { i: i, reused: s.page ? 'page' : (j.reused || null), free: j.free ? 1 : 0, cost: j.cost || 0, src: j.src || null,
                            up_ms: j.uploadMs || null, wait_ms: j.waitMs || null, dl_ms: j.dlMs || null, mb: j.mb || null, sec: Math.round(srvNds[i].sec) });
                    });
                } catch (_) {}
                srvRefresh(true);                               // the wallet and today's allowance moved
            }
        } catch (e) {
            if (gen !== runGen) return;
            var cancelled = e && e.name === 'AbortError';
            hideProgress();
            // a server split cannot be called back: it finishes, is kept in «Мои генерации», and the same
            // pair built again attaches to it for nothing — said, instead of a silent stop
            var srvLive = srvRun && srvT && srvT.state.some(function (s) { return s.job && s.p !== 'done'; });
            if (stalled) { window.__handleError(new Error(t(srvRun ? 'err_srv_timeout' : 'err_stalled')), SLUG); return; }
            if (cancelled) {
                feature('cancelled', { ms: Math.round(performance.now() - t0), srv: srvRun ? 1 : 0 });
                if (srvLive) window.__toast && window.__toast(t('srv_cancel_note'), 'info');
                return;
            }
            if (srvRun && e && e.kind) {
                try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'srv_split_failed', kind: e.kind, http: e.data && e.data.http || null, err: String(e.message || '').slice(0, 120) }); } catch (_) {}
                // the terms changed between the look and the press (the wallet, today's allowance, a
                // login that expired): the panel is re-read and says what now; nothing was charged
                if (/^(insufficient|free_exhausted|free_not_included|daily_cap|login_required|disabled)$/.test(e.kind)) {
                    window.__toast && window.__toast(t('err_srv_changed'), 'warning');
                } else if (e.kind === 'not_owned') {
                    // the stems the panel called yours were gone by the press (kept a week; or deleted)
                    window.__toast && window.__toast(t('err_srv_not_owned'), 'warning');
                } else {
                    window.__handleError(new Error(t(SRV_ERR[e.kind] || 'err_srv_failed')), SLUG);
                }
                srvRefresh(true);
                return;
            }
            var code = (e && e.code) || '', msg = String(e && e.message || e);
            try { console.error('[mashup-maker]', e); } catch (_) {}
            try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'build_failed', code: code || null, err: msg.slice(0, 140) }); } catch (_) {}
            // each failure says what it is: «on this device» was printed for a plan that could not lay
            // an a cappella on bars — the device was fine, the tempo was the question (QA 2026-09-24)
            var who = e.which === 'c' && C ? C : A, acapA = who.isStem, txt = /model_fetch|fetch|NetworkError/i.test(code + msg) ? t('err_model_net')
                : code === 'no_beat' ? t('err_no_beat', { name: ({ a: A, b: B, c: C }[e.which] || A).name })
                : code === 'no_vocals' ? t(acapA ? 'err_no_vocals_acap' : 'err_no_vocals', { name: who.name })
                : (code === 'no_plan' || code === 'no_anchors') && e.which === 'c' ? t('err_no_plan_c')
                : code === 'no_plan' || code === 'no_anchors' ? t(acapA ? 'err_no_plan_acap' : 'err_no_plan')
                : /worker_hung|WORKER_HUNG/.test(code) ? t('err_worker')
                : /warp_/.test(code + msg) ? t('err_render') : t('err_failed');
            // the stems came from the server and are paid for: the plan failing is not their loss
            if (srvRun && code !== 'server_stems') txt += ' ' + t('srv_kept_note');
            window.__handleError(new Error(txt), SLUG);
            if (/ep_ladder_exhausted|gpu_output_corrupt|not_wired/.test(code)) { off = code; }
        } finally {
            clearInterval(wd);
            if (unit) unit.abort();
            // the other song's polling and downloads stop with the run (the server finishes regardless)
            if (srvRun) { try { if (!ctl.signal.aborted) ctl.abort(); } catch (_) {} }
            if (running === ctl) running = null;
            if (!running) mode('is-running', false);
            $('mmCancel').hidden = true;
            syncGo();
        }
    }
    $('mmGo').addEventListener('click', function () { if (check) stopCheck(); run(); });
    $('mmCpu').addEventListener('click', function () {
        cpuChosen = true; feature('cpu_path', { why: off });
        // the model is fetched and checked now, while the person reads the price (no GPU: WASM)
        try {
            var M = window.MusionStemSeparator;
            if (M && M.preload) M.preload(SLUG, { allowWasm: true }).then(function (i) {
                cpuEtaSec = cpuEta(i && i.segMs);
                feature('sep_prewarm', { ep: i && i.provider, seg_ms: i && i.segMs, cpu: 1, eta_s: Math.round(cpuEtaSec) });
                syncGo();
            }, function () {});
        } catch (_) {}
        syncGo();
    });
    $('mmCancel').addEventListener('click', function () { if (running) { try { running.abort(); } catch (_) {} } });

    // ── the workspace ──────────────────────────────────────────────────────
    function resetWorkspace() {
        if (mixer) mixer.pause();
        if (running) { try { running.abort(); } catch (_) {} running = null; }
        runGen++; built = false; editWhich = 'a';
        if (engine) engine.reset();
        $('mmWs').hidden = true; hideProgress();
        mode('is-built', false); mode('is-running', false);
        prepared = null; paintShare();
    }
    function musicPreviewBuffer(tr) {
        // the waveform of the music lane: the enabled music stems summed at the timeline start — by the
        // timeline, per point it draws (`sum`); a full-length sum was 57 MB for a 5-min song (28.09)
        // — and as the timeline plays it: clips of the stems, the body again where the music goes round
        // under a longer vocal, over the span the music really lasts (not stretched to the mashup's end)
        var keysB = Object.keys(tr.stems).filter(function (k) { return k.indexOf('b:') === 0 && k !== 'b:vocals'; });
        if (!keysB.length) return null;
        var MX = window.MashupMixer, lead = tr.stems[keysB[0]], first = lead.buffer, end = MX.trackEnd(lead);
        return { duration: end, numberOfChannels: 1, length: Math.max(1, Math.round(end * first.sampleRate)), sampleRate: first.sampleRate,
                 sum: keysB.map(function (k) { return tr.stems[k].buffer; }), clips: MX.clipsOf(lead) };
    }
    /** Where the music starts over (a repeat of its body), on the timeline. */
    function seamsOf(tr) {
        var k = Object.keys(tr.stems).filter(function (x) { return x.indexOf('b:') === 0; })[0], g = k && tr.stems[k].segs;
        return g ? g.filter(function (x, i) { return i > 0 && Math.abs(x.from - (g[i - 1].from + g[i - 1].dur)) > 1e-6; }).map(function (x) { return x.at; }) : [];
    }
    function paintTimeline() {
        var E = getEngine(), pl = E.plan(), tr = E.tracks(), pc = E.planC();
        if (!pl || !tr) return;
        timeline.set({ duration: tr.duration, bars: E.timelineBars(), beats: Array.from(E.timelineBeats() || []), music: musicPreviewBuffer(tr), seams: seamsOf(tr), vocal: tr.vocal, startBar: pl.o,
            vocal2: tr.vocal2 || null, startBar2: pc ? pc.o : 0, active: editWhich, range: span === 'custom' ? rangeTimes(tr) : null });
        // a vocal moved by beats: the bar and the beat — «bar 5» alone would name the wrong place
        var beatOf = function (p) { return p && p.db ? ' · ' + t('placed_beat', { b: (p.db > 0 ? '+' : '−') + Math.abs(p.db) }) : ''; };
        $('mmPlaced').textContent = tr.vocal2 && pc ? t('placed_at_2', { a: pl.o + 1, c: pc.o + 1 }) : t('placed_at', { n: pl.o + 1 }) + beatOf(pl);
        // the music is shorter than the vocal from its landing: its bars go round, and the page says which
        var lp = E.arrangement(), ln = $('mmLoopNote');
        if (ln) { ln.hidden = !lp; ln.textContent = lp ? t('loop_note', { a: lp.l0 + 1, b: lp.l1 }) : ''; }
        $('mmTl').classList.toggle('three', !!tr.vocal2);
    }
    /** The plan of the vocal the studio edits now (A, or C on a three-song mashup). */
    function curPlan() { var E = getEngine(); return editWhich === 'c' && E.planC() ? E.planC() : E.plan(); }
    // «which vocal»: shown only on a three-song mashup; the key chips, the other bars, the fragment,
    // the nudge, the loop and the arrow keys all follow it
    function paintWhich() {
        var box = $('mmWhich'), on = withC();
        if (!box) return;
        box.hidden = !on;
        if (!on && editWhich === 'c') editWhich = 'a';
        Array.prototype.forEach.call(box.querySelectorAll('[data-which]'), function (x) { var s = x.dataset.which === editWhich; x.classList.toggle('on', s); x.setAttribute('aria-pressed', s ? 'true' : 'false'); });
    }
    function setWhich(w, why) {
        w = w === 'c' && withC() ? 'c' : 'a';
        if (w === editWhich) { paintWhich(); return; }
        editWhich = w;
        paintWhich(); paintKey(); paintRegion(); paintOffset(); paintPos(); syncLoop();
        if (timeline) timeline.set({ active: w });
        if (why) feature('which', { w: w, why: why });
    }
    function paintKey() {
        var pl = curPlan(); if (!pl) return;
        var isC = pl !== getEngine().plan();
        var max = -Infinity, min = Infinity;
        pl.byShift.forEach(function (x) { max = Math.max(max, x.score); min = Math.min(min, x.score); });
        var box = $('mmKeyChips'), html = '';
        pl.byShift.forEach(function (x) {
            var h = max > min ? (x.score - min) / (max - min) : 0.5, on = x.shift === pl.shift;
            html += '<button type="button" class="mm-kc' + (on ? ' on' : '') + '" data-shift="' + x.shift + '" aria-pressed="' + (on ? 'true' : 'false') + '" aria-label="' + t('key_semis', { n: signed(x.shift) }) + '"><i style="height:' + Math.round(8 + h * 36) + 'px"></i><span dir="ltr">' + (x.shift > 0 ? '+' : x.shift < 0 ? '−' : '') + Math.abs(x.shift) + '</span></button>';
        });
        box.innerHTML = html;
        // C's vocal carries the band's move for A too (MashupPlan.planSecond): its note speaks of the vocal only
        var note = pl.rap ? t('key_rap', { n: signed(pl.byShift.slice().sort(function (a, b) { return b.score - a.score; })[0].shift) })
            : isC ? (Math.abs(pl.vocalShift) > 0.05 ? t('key_vocal_only', { v: signed(pl.vocalShift) }) : t('key_none'))
            : pl.vocalShift && pl.instShift ? t('key_split', { v: signed(pl.vocalShift), i: signed(pl.instShift) })
            : pl.instShift ? t('key_music_only', { i: signed(pl.instShift) })
            : Math.abs(pl.vocalShift) > 0.05 ? t('key_vocal_only', { v: signed(pl.vocalShift) }) : t('key_none');
        if (Math.abs(pl.tuneShift) >= 0.1) note += ' ' + t('key_tuning', { c: Math.round(pl.tuneShift * 100) });
        if (Math.abs(pl.vocalShift) > 2.05) note += ' ' + t('key_far_warn');
        if (isC) note += ' ' + t('key_c_note');
        $('mmKeyNote').textContent = note;
        $('mmWhoWrap').hidden = isC;
        var lo = isC ? lastPlanOptsC : lastPlanOpts;
        $('mmKeyAuto').classList.toggle('on', !lo.forceShift && lo.forceShift !== 0);
        pl = getEngine().plan();                           // the tempo line is the music's
        $('mmTempoNow').textContent = t('tempo_now', { bpm: Math.round(pl.bpmOut * 10) / 10 }) + ' · ' + t('tempo_stretch', { pv: fmtPctSigned(pl.bpmOut / (pl.bpmA * pl.k) - 1), pm: fmtPctSigned(pl.bpmOut / pl.bpmB - 1) });
        var alts = $('mmAlts'); alts.innerHTML = '';
        pl = curPlan();
        (pl.alternatives || []).forEach(function (z) {
            var b = document.createElement('button'); b.type = 'button'; b.className = 'tc-btn ghost sm';
            b.textContent = t('alt_bar', { n: z.o + 1 }); b.dataset.o = z.o; b.dataset.a0 = z.a0; b.dataset.a1 = z.a1;
            alts.appendChild(b);
        });
        $('mmAltsWrap').hidden = !(pl.alternatives && pl.alternatives.length);
    }
    function paintTracks() {
        var E = getEngine(), A = E.song('a'), B = E.song('b'), C = withC() ? E.song('c') : null, st = mixer.state();
        var rows = [['vocals', 'stem_vocals'], ['drums', 'stem_drums'], ['bass', 'stem_bass'], ['other', 'stem_other']];
        var html = '', groups = [['a', A, 'col_a'], ['b', B, 'col_b']];
        if (C) groups.splice(1, 0, ['c', C, 'col_c']);
        groups.forEach(function (g) {
            var k = g[0], s = g[1];
            html += '<div class="mm-sg"><p class="mm-sg-h"><span class="mm-tag' + (k !== 'a' ? ' ' + k : '') + '" aria-hidden="true">' + k.toUpperCase() + '</span>' + t(g[2]) + '</p>';
            rows.forEach(function (r) {
                // the second singer gives its voice only
                if (!(s.stems && s.stems[r[0]]) || (k === 'c' && r[0] !== 'vocals')) return;
                var key = k + ':' + r[0], on = st.on[key] != null ? st.on[key] : defaultOn(key), gv = st.gains[key] != null ? st.gains[key] : 1;
                var id = 'mmSt_' + k + '_' + r[0];
                html += '<div class="mm-srow"><input type="checkbox" id="' + id + '" data-stem="' + key + '"' + (on ? ' checked' : '') + '>'
                    + '<label for="' + id + '"><span>' + t(r[1]) + '</span></label>'
                    + '<input type="range" class="tc-range" min="0" max="150" step="1" value="' + Math.round(gv * 100) + '" data-gain="' + key + '" aria-label="' + t('volume_for', { name: t(r[1]) + ' (' + k.toUpperCase() + ')' }) + '"></div>';
            });
            html += '</div>';
        });
        $('mmStems').innerHTML = html;
    }
    function defaultOn(key) { return key === 'a:vocals' || key === 'c:vocals' || key === 'b:drums' || key === 'b:bass' || key === 'b:other'; }
    function initialMixerState() {
        var on = {};
        ['a', 'b'].forEach(function (k) { ['vocals', 'drums', 'bass', 'other'].forEach(function (s) { on[k + ':' + s] = defaultOn(k + ':' + s); }); });
        on['c:vocals'] = true;
        return on;
    }

    var lastPlanOpts = {}, lastPlanOptsC = {}, wsShownAt = 0;

    // ── the vocal fragment: from which phrase group, how many bars ──────────────────────────
    // The plan's own `starts` (phrase groups of A after a gap of a bar or more) are the natural
    // places a section begins; they are taken from the FIRST plan of a build — a forced region
    // narrows later plans' `starts` to itself.
    var regBase = null, regBaseC = null;
    function curRegBase() { return editWhich === 'c' && withC() ? regBaseC : regBase; }
    function paintRegion() {
        var E = getEngine(), pl = curPlan(), A = E.song(editWhich === 'c' && withC() ? 'c' : 'a'), box = $('mmRegion'), regBase = curRegBase();
        if (!pl || !A || !regBase || !regBase.starts.length) { box.hidden = true; return; }
        var bars = window.MashupPlan.barsOf(A.grid), tAt = function (b) { return b < bars.length ? bars[b].t : A.duration; };
        // no separate «whole vocal» chip: the first start IS the vocal's beginning, and «first start +
        // to the end» is the whole vocal — a second chip for the same bar read as two different places
        var full = pl.a0 === regBase.a0Full && pl.a1 === regBase.a1Full, html = '';
        regBase.starts.forEach(function (b) {
            var on = pl.a0 === b;
            // the first start is the vocal's own beginning — the second its first sound is heard on, which for
            // an a cappella file is the file's, not the bar line its singer «comes in» on
            html += '<button type="button" class="tc-seg-b' + (on ? ' on' : '') + '" data-rs="' + b + '" aria-pressed="' + (on ? 'true' : 'false') + '" dir="ltr">' + fmtTime(b === regBase.a0Full && regBase.t0Full != null ? regBase.t0Full : tAt(b)) + '</button>';
        });
        $('mmRegStart').innerHTML = html;
        // one phrase group (a take sung through without a pause) leaves nothing to choose: only the length
        $('mmRegStart').parentNode.hidden = regBase.starts.length < 2;
        var len = pl.a1 >= regBase.a1Full ? 'end' : String(pl.a1 - pl.a0);
        Array.prototype.forEach.call(document.querySelectorAll('#mmRegLen [data-rl]'), function (x) { var on = x.dataset.rl === len; x.classList.toggle('on', on); x.setAttribute('aria-pressed', on ? 'true' : 'false'); });
        // no dir=ltr and no isolate: in Arabic the range reads right-to-left like the sentence around it
        // (0:34 on the right, read first) — a forced ltr span broke the sentence itself (QA 24.09, ar iPhone)
        // the seconds are those of what is HEARD (plan.extent): the vocal runs from its first sound to its
        // last, which the bar lines of the region only approximate
        var ex = pl.extent || { t0: tAt(pl.a0), t1: tAt(pl.a1) };
        $('mmRegVal').textContent = (full ? t('region_full') + ' · ' : '') + t('region_val', { a: pl.a0 + 1, b: pl.a1, t: fmtTime(ex.t0) + '–' + fmtTime(ex.t1) });
        box.hidden = false;
    }
    function applyRegion(start, len) {
        var pl = curPlan(), regBase = curRegBase(); if (!pl || !regBase) return;
        var a0 = parseInt(start, 10);
        var a1 = len === 'end' ? regBase.a1Full : Math.min(regBase.a1Full, a0 + parseInt(len, 10));
        if (!(a1 - a0 >= 1)) return;
        feature('region', { start: a0, first: a0 === regBase.a0Full ? 1 : 0, len: len, bars: a1 - a0, w: editWhich });
        act({ forceRegion: [a0, a1], forceBar: pl.o, forceBeat: pl.db || 0 }, 'region', editWhich);
    }
    $('mmRegStart').addEventListener('click', function (e) {
        var b = e.target.closest('[data-rs]'); if (!b) return;
        Array.prototype.forEach.call(this.querySelectorAll('[data-rs]'), function (x) { x.classList.toggle('on', x === b); x.setAttribute('aria-pressed', x === b ? 'true' : 'false'); });
        // a new start keeps the chosen length («the next 16 bars from here»)
        var pl = curPlan(), rb = curRegBase(), len = pl && rb && pl.a1 < rb.a1Full ? String(pl.a1 - pl.a0) : 'end';
        applyRegion(b.dataset.rs, /^(8|16|32)$/.test(len) ? len : 'end');
    });
    $('mmRegLen').addEventListener('click', function (e) {
        var b = e.target.closest('[data-rl]'); if (!b) return;
        Array.prototype.forEach.call(this.querySelectorAll('[data-rl]'), function (x) { x.classList.toggle('on', x === b); x.setAttribute('aria-pressed', x === b ? 'true' : 'false'); });
        var pl = curPlan(); if (!pl || !curRegBase()) return;
        applyRegion(String(pl.a0), b.dataset.rl);              // same start, new length
    });
    async function showWorkspace() {
        var E = getEngine();
        if (!mixer) {
            mixer = window.MashupMixer.create({ ctx: audioCtx(), workletUrl: CFG.duckUrl });
            mixer.onTick(function (p) { timeline.playhead(p); $('mmTime').textContent = fmtTime(p) + ' / ' + fmtTime(mixer.duration()); });
            mixer.onEnd(function () { setPlayUi(false); timeline.playhead(0); });
            mixer.set({ on: initialMixerState() });
        }
        if (!timeline) timeline = window.MashupTimeline.create($('mmTl'), {
            label: t('tl_label'), valueText: function (n, w) { return w ? t('bar_n_which', { w: w.toUpperCase(), n: n }) : t('bar_n', { n: n }); },
            lanes: { vocal: 'A · ' + t('lane_vocal'), vocal2: 'C · ' + t('lane_vocal'), music: 'B · ' + t('lane_music') },
            onSeek: function (tt) { mixer.seek(tt); timeline.playhead(tt); },
            snap: snapDt, onMove: onMove, onTrim: onTrim, onKey: onKeyMove, onRange: onRange, onSelect: function (w) { setWhich(w, 'timeline'); }
        });
        // a new build starts clean: no nudge (before the mixer gets the tracks, or it keeps the old one)
        // and none of the previous pair's forced choices — its region, bar and key belonged to other songs
        // and came back on the first key or tempo change (the build plans with defaults)
        getEngine().setVocalOffset(0); getEngine().setVocalOffset(0, 'c'); $('mmOffset').value = 0;
        lastPlanOpts = {}; lastPlanOptsC = {}; editWhich = 'a'; undoStack = []; redoStack = []; paintUndo();
        // the edges belonged to the previous pair's bars
        if (span === 'custom') { span = 'full'; paintSpanSeg(); }
        rangeBars = null;
        [['mmTempo', 'data-tempo', 'b'], ['mmWho', 'data-who', 'auto']].forEach(function (g) {
            Array.prototype.forEach.call($(g[0]).querySelectorAll('[' + g[1] + ']'), function (x) { var on = x.getAttribute(g[1]) === g[2]; x.classList.toggle('on', on); x.setAttribute('aria-pressed', on ? 'true' : 'false'); });
        });
        $('mmBpmCustomWrap').hidden = true;
        pushTracks();
        hideProgress();
        // setup → studio: the decks fold to their file rows as the result appears (a cross-fade where
        // the browser has view transitions and motion is welcome; an instant switch everywhere else)
        var enter = function () { $('mmWs').hidden = false; mode('is-built', true); };
        if (window.__viewTransition && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) window.__viewTransition(enter); else enter();
        paintWhich(); paintTimeline(); paintKey(); paintTracks(); paintOffset(); paintPos(); paintStemsLock(); paintShare(); paintReel();
        $('mmTime').textContent = fmtTime(0) + ' / ' + fmtTime(mixer.duration());
        try { $('mmWs').scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch (_) {}
        var pl0 = E.plan(), pc0 = E.planC();
        regBase = pl0 ? { starts: (pl0.starts || []).slice(), a0Full: pl0.a0Full, a1Full: pl0.a1Full, t0Full: pl0.full && pl0.extent ? pl0.extent.t0 : null } : null;
        regBaseC = pc0 ? { starts: (pc0.starts || []).slice(), a0Full: pc0.a0Full, a1Full: pc0.a1Full, t0Full: pc0.full && pc0.extent ? pc0.extent.t0 : null } : null;
        paintRegion();
        wsShownAt = performance.now();
        stage('ready');
        nextRefresh();
    }

    // ONE replan at a time; clicks during it only update the options, and the running one
    // renders again with the LATEST of them when it finishes. Overlapping engine.replan() calls
    // used to queue every intermediate render on the workers (five key chips = five full renders
    // before the fifth was heard) and shared the engine's one `plan` between them.
    var replanBusy = false, replanAgain = null, replanIdleP = Promise.resolve(), replanIdleRes = null;
    /** Resolves when no replan is running: an export or a stem render must not use the tracks
     *  of the key the chip no longer shows (the chip changes at the tap, the sound seconds later). */
    function replanIdle() { return replanIdleP; }
    var dirtyA = false, dirtyC = false;
    async function replan(o, why, which) {
        if (which === 'c') { lastPlanOptsC = Object.assign({}, lastPlanOptsC, o); dirtyC = true; }
        else { lastPlanOpts = Object.assign({}, lastPlanOpts, o); dirtyA = true; }
        $('mmRebuild').hidden = false;
        if (replanBusy) { replanAgain = why || 'queued'; feature('replan_coalesced', { why: why || null }); return; }
        replanBusy = true;
        replanIdleP = new Promise(function (r) { replanIdleRes = r; });
        var E = getEngine(), gen = runGen;
        try {
            do {
                var reason = replanAgain || why; replanAgain = null;
                var t0 = performance.now();
                // A first (C follows the music's tempo and key), then C's own choices
                if (dirtyA) { dirtyA = false; await E.replan(lastPlanOpts); }
                if (gen !== runGen) return;                              // a new build replaced this workspace
                if (replanAgain) continue;                               // a newer choice arrived: render that one first
                // «follow A» (Auto, an undo to a C that followed) is an order for this render, not a standing option
                if (dirtyC) { dirtyC = false; var oc = lastPlanOptsC; delete lastPlanOptsC.follow; await E.replanC(oc); }
                if (gen !== runGen) return;
                if (replanAgain) continue;
                E.applyClean($('mmClean').checked);
                // a playing preview carries on from where it is now, in the new key or tempo (the
                // mixer restarts itself on new tracks); it used to jump back to where the render
                // began and to start again even when the person had paused meanwhile
                pushTracks();
                paintTimeline(); paintKey(); paintTracks(); paintRegion(); paintWhich(); paintPos();
                feature('replan', { why: reason || null, shift: E.plan().shift, bar: E.plan().o, tempo: E.plan().tempoMode, ms: Math.round(performance.now() - t0), w: which || 'a', loop: E.arrangement() ? E.arrangement().n : 0 });
            } while (replanAgain);
        } catch (e) {
            try { console.error('[mashup replan]', e); } catch (_) {}
            if (gen === runGen && e && e.code === 'no_plan') {
                // the engine refused the edit and kept the mashup in force: the panels go back to it,
                // and the next edit starts from its options, not from the impossible one
                lastPlanOpts = E.planOpts('a'); lastPlanOptsC = E.planOpts('c');
                paintKey(); paintRegion(); paintPos(); paintWhich();
                feature('replan_refused', { why: why || null, w: e.which || 'a' });
                window.__toast(t('err_no_plan_edit'), 'warning');
            } else if (gen === runGen) window.__handleError(new Error(t('err_render')), SLUG);
        } finally {
            replanBusy = false; replanAgain = null; dirtyA = false; dirtyC = false;
            if (gen === runGen) $('mmRebuild').hidden = true;
            if (replanIdleRes) { replanIdleRes(); replanIdleRes = null; }
        }
    }

    // ── where the vocal stands: bars, beats, a fine nudge — by buttons, keys and the timeline ──────────
    // Comment #1941 (18.10.2026, «I want to set it up MANUALLY»), and the field before it: 551 of 690 builds
    // went on to the manual controls, one nudge in five (324 of 1630) was pushed to the end of its ±½-beat
    // range, and a move by a whole beat was Shift+arrow on a focused canvas only (13 uses in three weeks) —
    // a vocal sitting a beat off had no way to its place by mouse or finger. Now a move is bars + beats +
    // a nudge: the bars and beats go into the plan (forceBar/forceBeat — through the warp, onto the music's
    // own beats), the nudge stays a time shift within half a beat. A beat moved as time missed a live
    // drummer's beat by over 20 ms at p90 on 5 of 15 corpus songs (tests/js/mashup-maker-position.test.mjs).
    var SNAPS = { bar: 1, beat: 1, free: 1 }, snapMode = 'bar';
    try { var sv = window.__toolSettings && window.__toolSettings.load(SLUG, 'snap', 'bar'); if (SNAPS[sv]) snapMode = sv; } catch (_) {}
    function whichOf(w) { return w === 'c' && withC() ? 'c' : 'a'; }
    /** Move vocal `w` by bars and beats (heard at once, fitted exactly a moment later) and/or set its nudge. */
    function applyMove(w, bars, beats, fine, src) {
        var E = getEngine(), pl = w === 'c' ? E.planC() : E.plan();
        if (!pl || !E.rendered()) return;
        var fineChanged = fine != null && Math.abs(fine - E.vocalOffset(w)) > 1e-4;
        if (!bars && !beats && !fineChanged) return;
        pushUndo();
        if (bars || beats) E.nudgePreview(bars || 0, w, beats || 0);
        if (fineChanged) E.setVocalOffset(fine, w);
        pushTracks(); paintTimeline(); paintOffset(); paintPos();
        if (bars) feature('move_bars', { d: bars, bar: pl.o, w: w, src: src });
        if (beats) feature('move_beats', { d: beats, db: pl.db || 0, w: w, src: src });
        if (fineChanged && src !== 'slider') feature('offset', { ms: Math.round(E.vocalOffset(w) * 1000), src_a: (E.song('a') || {}).tempoSrc || 'model', w: w, src: src });
        if (!bars && !beats) return;
        clearTimeout(pendingMove);
        // then the exact fit onto the new bar and beat (B's grid may drift); the fragment stays
        pendingMove = setTimeout(function () {
            var p2 = w === 'c' ? E.planC() : E.plan();
            if (p2) replan({ forceBar: p2.o, forceBeat: p2.db || 0, forceRegion: [p2.a0, p2.a1] }, beats ? 'move_beat' : 'move', w);
        }, 700);
    }
    /** What the timeline draws while a block is dragged: the page's snap, as a distance on the timeline. */
    function snapDt(dt, which, kind) {
        if (kind === 'r0' || kind === 'r1') { var rd = rangeDrag(kind === 'r0' ? 0 : 1, dt); return rd ? rd.dt : dt; }
        var P = window.MashupPlan, p = getEngine().placement(whichOf(which));
        if (!p) return dt;
        if (kind === 'move') {
            var g = P.dragTarget(dt, snapMode, { beatSec: p.beatSec, meter: p.meterB, fine: p.fine });
            return (g.bars * p.meterB + g.beats) * p.beatSec + (g.fine - p.fine);
        }
        var r = P.trimTarget(kind === 'trim0' ? 0 : 1, dt, p), per = p.meterA * p.k * p.beatSec;
        return kind === 'trim0' ? (r.a0 - p.a0) * per : (r.a1 - p.a1) * per;
    }
    function onMove(m) {
        var w = whichOf(m.which);
        if (w !== editWhich) setWhich(w, 'drag');              // the vocal being moved is the one the controls edit
        if (!m.final) return;                                  // the canvas shows the drag itself
        var p = getEngine().placement(w); if (!p) return;
        var g = window.MashupPlan.dragTarget(m.dt, snapMode, { beatSec: p.beatSec, meter: p.meterB, fine: p.fine });
        applyMove(w, g.bars, g.beats, snapMode === 'free' ? g.fine : null, 'drag');
    }
    /** An edge of the vocal dragged: the fragment by the singer's bars; what stays keeps sounding where it was. */
    function onTrim(m) {
        var w = whichOf(m.which), p = getEngine().placement(w); if (!p) return;
        var r = window.MashupPlan.trimTarget(m.edge, m.dt, p);
        if (r.a0 === p.a0 && r.a1 === p.a1) return;
        clearTimeout(pendingMove);
        feature('trim', { edge: m.edge, d: m.edge ? r.a1 - p.a1 : r.a0 - p.a0, bars: r.a1 - r.a0, w: w });
        act({ forceRegion: [r.a0, r.a1], forceBar: p.o, forceBeat: p.db + r.beats }, 'trim', w);
    }
    function onKeyMove(m) {
        var w = whichOf(m.which), E = getEngine();
        if (m.fine) {
            var p = E.placement(w), half = p ? p.beatSec / 2 : 0.25;
            applyMove(w, 0, 0, Math.max(-half, Math.min(half, E.vocalOffset(w) + m.fine)), 'key');
        } else applyMove(w, m.bars, m.beats, null, 'key');
    }
    function paintPos() {
        var p = getEngine().placement(editWhich);
        if (!p) return;
        $('mmBarV').textContent = String(p.o + 1);
        $('mmBeatV').textContent = p.db > 0 ? '+' + p.db : p.db < 0 ? '−' + Math.abs(p.db) : '0';
        $('mmPosAuto').classList.toggle('on', !hasPin(editWhich === 'c' ? lastPlanOptsC : lastPlanOpts) && Math.abs(getEngine().vocalOffset(editWhich)) < 1e-4);
    }
    function hasPin(o) { return o.forceBar != null || !!o.forceBeat; }
    function paintSnap() {
        Array.prototype.forEach.call(document.querySelectorAll('#mmSnap [data-snap]'), function (x) { var on = x.dataset.snap === snapMode; x.classList.toggle('on', on); x.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    }
    $('mmSnap').addEventListener('click', function (e) {
        var b = e.target.closest('[data-snap]'); if (!b || !SNAPS[b.dataset.snap]) return;
        snapMode = b.dataset.snap; paintSnap();
        try { window.__toolSettings && window.__toolSettings.save(SLUG, 'snap', snapMode); } catch (_) {}
        feature('snap', { v: snapMode });
    });
    paintSnap();
    $('mmPos').addEventListener('click', function (e) {
        var b = e.target.closest('[data-step]'); if (!b) return;
        var d = parseInt(b.dataset.d, 10);
        applyMove(editWhich, b.dataset.step === 'bar' ? d : 0, b.dataset.step === 'beat' ? d : 0, null, 'btn');
    });
    // «Auto»: the place the plan itself chooses for this fragment — no bar, no beat, no nudge of the person's
    $('mmPosAuto').addEventListener('click', function () {
        var E = getEngine(), w = editWhich;
        if (!E.plan()) return;
        clearTimeout(pendingMove);
        pushUndo();
        E.setVocalOffset(0, w);
        feature('pos_auto', { w: w });
        replan(w === 'c' ? { follow: true, forceBar: null, forceBeat: null } : { forceBar: null, forceBeat: null }, 'pos_auto', w);
    });

    // ── undo / redo of what the person changed in the arrangement (place, fragment, key, tempo) ──────
    // A plan IS its options: a step back is the options before the step, planned again. Every option the
    // plan reads is written out (null = the plan's own choice) — the engine MERGES what it is given.
    var PLAN_KEYS = ['forceShift', 'forceBar', 'forceBeat', 'forceRegion', 'tempoMode', 'bpm', 'shiftPolicy'];
    var undoStack = [], redoStack = [], UNDO_MAX = 60;
    function snapshot() {
        var E = getEngine();
        return { a: Object.assign({}, lastPlanOpts), c: Object.assign({}, lastPlanOptsC), offA: E.vocalOffset('a'), offC: E.vocalOffset('c'), cAuto: E.cAuto(),
                 span: span, range: rangeBars ? { b0: rangeBars.b0, b1: rangeBars.b1 } : null };
    }
    function pushUndo() { undoStack.push(snapshot()); if (undoStack.length > UNDO_MAX) undoStack.shift(); redoStack = []; paintUndo(); }
    function written(o) {
        var r = {};
        PLAN_KEYS.forEach(function (k) { r[k] = o[k] != null ? o[k] : null; });
        if (!r.tempoMode) r.tempoMode = 'b';
        if (!r.shiftPolicy) r.shiftPolicy = 'auto';
        return r;
    }
    function restore(s, why) {
        var E = getEngine();
        clearTimeout(pendingMove);
        lastPlanOpts = written(s.a);
        lastPlanOptsC = written(s.c);
        if (s.cAuto) lastPlanOptsC.follow = true;
        E.setVocalOffset(s.offA, 'a'); E.setVocalOffset(s.offC, 'c');
        span = s.span || 'full'; rangeBars = s.range ? { b0: s.range.b0, b1: s.range.b1 } : null;
        paintChoices(); paintOffset(); paintSpanSeg(); paintSpan();
        if (withC()) dirtyC = true;
        replan({}, why);
    }
    function undo() { if (!undoStack.length || !getEngine().plan()) return; redoStack.push(snapshot()); restore(undoStack.pop(), 'undo'); paintUndo(); feature('undo', { n: undoStack.length }); }
    function redo() { if (!redoStack.length || !getEngine().plan()) return; undoStack.push(snapshot()); restore(redoStack.pop(), 'redo'); paintUndo(); feature('redo', { n: redoStack.length }); }
    function paintUndo() { $('mmUndo').disabled = !undoStack.length; $('mmRedo').disabled = !redoStack.length; }
    $('mmUndo').addEventListener('click', undo);
    $('mmRedo').addEventListener('click', redo);
    document.addEventListener('keydown', function (e) {
        if (!(e.ctrlKey || e.metaKey) || e.altKey || !built || $('mmWs').hidden) return;
        var k = String(e.key || '').toLowerCase(), isUndo = k === 'z' && !e.shiftKey, isRedo = (k === 'z' && e.shiftKey) || k === 'y';
        if (!isUndo && !isRedo) return;
        if (window.__keyOwnedByFocus && window.__keyOwnedByFocus(e)) return;   // a field's own undo
        e.preventDefault();
        if (isUndo) undo(); else redo();
    });
    /** A choice the person makes in the arrangement: undoable, then planned. */
    function act(o, why, which) { pushUndo(); return replan(o, why, which); }
    /** The tempo and «what moves» chips as the options say — after an undo they change without a click. */
    function paintChoices() {
        var o = lastPlanOpts, tm = o.tempoMode || 'b', who = o.shiftPolicy || 'auto';
        [['mmTempo', 'data-tempo', tm], ['mmWho', 'data-who', who]].forEach(function (g) {
            Array.prototype.forEach.call($(g[0]).querySelectorAll('[' + g[1] + ']'), function (x) { var on = x.getAttribute(g[1]) === g[2]; x.classList.toggle('on', on); x.setAttribute('aria-pressed', on ? 'true' : 'false'); });
        });
        $('mmBpmCustomWrap').hidden = tm !== 'custom';
        if (tm === 'custom' && o.bpm) $('mmBpmCustom').value = o.bpm;
    }

    // ── the inspector: four groups (key · tempo · tracks · mix), one on screen ─────────────
    // Stacked, the five panels were 1 150 px of rail beside a 400-px result (1440×900, 24.09);
    // a tab strip keeps every control one tap away and the studio inside one screen. The tab a
    // person works in is remembered — people come back to the key, or to the mix, not to both.
    var TABS = { key: 'mmPKey', tempo: 'mmPTempo', tracks: 'mmPTracks', mix: 'mmPMix' };
    function setTab(name, focus) {
        if (!TABS[name]) name = 'key';
        Array.prototype.forEach.call(document.querySelectorAll('#mmTabs [data-tab]'), function (b) {
            var on = b.dataset.tab === name;
            b.classList.toggle('on', on); b.setAttribute('aria-selected', on ? 'true' : 'false'); b.tabIndex = on ? 0 : -1;
            if (on && focus) b.focus();
        });
        Object.keys(TABS).forEach(function (k) { $(TABS[k]).hidden = k !== name; });
        try { window.__toolSettings && window.__toolSettings.save(SLUG, 'tab', name); } catch (_) {}
    }
    $('mmWhich').addEventListener('click', function (e) {
        var b = e.target.closest('[data-which]'); if (!b) return;
        setWhich(b.dataset.which, 'button');
    });
    $('mmTabs').addEventListener('click', function (e) {
        var b = e.target.closest('[data-tab]'); if (!b) return;
        setTab(b.dataset.tab); feature('tab', { t: b.dataset.tab });
    });
    $('mmTabs').addEventListener('keydown', function (e) {
        var order = Object.keys(TABS), cur = order.indexOf((document.activeElement && document.activeElement.dataset && document.activeElement.dataset.tab) || '');
        if (cur < 0) return;
        var rtl = getComputedStyle(this).direction === 'rtl', next = null;
        if (e.key === 'ArrowRight') next = cur + (rtl ? -1 : 1); else if (e.key === 'ArrowLeft') next = cur + (rtl ? 1 : -1);
        else if (e.key === 'Home') next = 0; else if (e.key === 'End') next = order.length - 1;
        if (next == null) return;
        e.preventDefault();
        setTab(order[(next + order.length) % order.length], true);
    });
    try { setTab((window.__toolSettings && window.__toolSettings.load(SLUG, 'tab', 'key')) || 'key'); } catch (_) { setTab('key'); }

    /* The bar and region the plan stands on now, pinned so a key / tempo / «who»
       change keeps the place. There is no plan while a rebuild is under way (after
       swapping A and B the engine drops it) — reading `.o` of null threw from the
       chip (Sentry TIMBRICA-FRONTEND-PP); then nothing is pinned and the choice
       rides into the next plan. */
    function pinNow() {
        var p = getEngine().plan();
        return p ? { forceBar: p.o, forceBeat: p.db || 0, forceRegion: [p.a0, p.a1] } : {};
    }

    // panels
    $('mmKeyChips').addEventListener('click', function (e) {
        var b = e.target.closest('[data-shift]'); if (!b) return;
        // the chip answers the tap at once; the render behind it takes seconds
        Array.prototype.forEach.call(this.querySelectorAll('[data-shift]'), function (x) { x.classList.toggle('on', x === b); x.setAttribute('aria-pressed', x === b ? 'true' : 'false'); });
        // C keeps following A's end unless the person moved it — its key alone changes here
        if (editWhich === 'c' && withC()) { act({ forceShift: parseInt(b.dataset.shift, 10) }, 'key', 'c'); return; }
        act(Object.assign({ forceShift: parseInt(b.dataset.shift, 10) }, pinNow()), 'key');
    });
    $('mmKeyAuto').addEventListener('click', function () { act({ forceShift: null }, 'key_auto', editWhich === 'c' && withC() ? 'c' : 'a'); });
    $('mmWho').addEventListener('click', function (e) {
        var b = e.target.closest('[data-who]'); if (!b) return;
        Array.prototype.forEach.call(this.querySelectorAll('[data-who]'), function (x) { x.classList.toggle('on', x === b); x.setAttribute('aria-pressed', x === b ? 'true' : 'false'); });
        act(Object.assign({ shiftPolicy: b.dataset.who }, pinNow()), 'who');
    });
    $('mmTempo').addEventListener('click', function (e) {
        var b = e.target.closest('[data-tempo]'); if (!b) return;
        Array.prototype.forEach.call(this.querySelectorAll('[data-tempo]'), function (x) { x.classList.toggle('on', x === b); x.setAttribute('aria-pressed', x === b ? 'true' : 'false'); });
        var mode = b.dataset.tempo;
        $('mmBpmCustomWrap').hidden = mode !== 'custom';
        if (mode === 'custom') { var pl = getEngine().plan(); if (pl) $('mmBpmCustom').value = Math.round(pl.bpmOut * 10) / 10; $('mmBpmCustom').focus({ preventScroll: true }); return; }
        act(Object.assign({ tempoMode: mode }, pinNow()), 'tempo');
    });
    $('mmBpmCustomGo').addEventListener('click', function () {
        var v = parseFloat($('mmBpmCustom').value);
        if (v >= 50 && v <= 220) act(Object.assign({ tempoMode: 'custom', bpm: v }, pinNow()), 'tempo_custom');
    });
    $('mmAlts').addEventListener('click', function (e) {
        var b = e.target.closest('[data-o]'); if (!b) return;
        act({ forceBar: parseInt(b.dataset.o, 10), forceRegion: [parseInt(b.dataset.a0, 10), parseInt(b.dataset.a1, 10)] }, 'alt', editWhich === 'c' && withC() ? 'c' : 'a');
    });
    $('mmStems').addEventListener('change', async function (e) {
        var cb = e.target.closest('[data-stem]'); if (!cb) return;
        var key = cb.dataset.stem, on = cb.checked, patch = {}; patch[key] = on;
        var lazyA = on && key.indexOf('a:') === 0 && key !== 'a:vocals';
        var lazyB = on && key.indexOf('b:') === 0 && !(getEngine().rendered() && getEngine().rendered().bStems[key.slice(2)]);
        if (lazyA || lazyB) {
            $('mmRebuild').hidden = false;
            try {
                await replanIdle();                              // render the stem onto the tracks that will stay
                if (lazyA) await getEngine().renderAStem(key.slice(2)); else await getEngine().renderBStem(key.slice(2));
                pushTracks();
            } catch (_) { window.__handleError(new Error(t('err_render')), SLUG); cb.checked = false; if (!replanBusy) $('mmRebuild').hidden = true; return; }
            if (!replanBusy) $('mmRebuild').hidden = true;
        }
        if (key.indexOf('b:') === 0) {
            var st0 = mixer.state(), names = [];
            ['vocals', 'drums', 'bass', 'other'].forEach(function (n) { var kk = 'b:' + n; var o = kk === key ? on : (st0.on[kk] != null ? st0.on[kk] : defaultOn(kk)); if (o) names.push(n); });
            getEngine().setNeededB(names);
        }
        mixer.set({ on: patch }); feature('stem', { k: key, on: on ? 1 : 0 });
        if (key.indexOf('b:') === 0) paintTimeline();
    });
    $('mmStems').addEventListener('input', function (e) {
        var r = e.target.closest('[data-gain]'); if (!r) return;
        var patch = {}; patch[r.dataset.gain] = parseInt(r.value, 10) / 100; mixer.set({ gains: patch });
    });
    $('mmVocalDb').addEventListener('input', function () { mixer.set({ vocalDb: parseFloat(this.value) }); $('mmVocalDbV').textContent = signed(parseFloat(this.value)) + ' dB'; });
    $('mmReverb').addEventListener('input', function () { mixer.set({ reverb: parseInt(this.value, 10) / 100 }); });
    // settled values only (`change`), not every step of a drag: what people end up choosing
    $('mmVocalDb').addEventListener('change', function () { feature('mix_set', { p: 'vocal_db', v: parseFloat(this.value) }); });
    $('mmReverb').addEventListener('change', function () { feature('mix_set', { p: 'reverb', v: parseInt(this.value, 10) }); });
    // the whole vocal, a hair earlier or later (± half a beat): the engine applies it to every output
    function paintOffset() {
        var E = getEngine(), sec = E.vocalOffset(editWhich), pl = E.plan();
        // the slider shows the edited vocal's own nudge (a share of a beat)
        if (pl && pl.bpmOut > 0) $('mmOffset').value = Math.round(sec / (60 / pl.bpmOut) * 100);
        var ms = Math.round(sec * 1000);
        $('mmOffsetV').textContent = (ms > 0 ? '+' : ms < 0 ? '−' : '') + Math.abs(ms) + ' ' + t('ms');
    }
    // one drag of the slider is one step back for undo: the state before its first move is kept
    var sliding = false;
    $('mmOffset').addEventListener('input', function () {
        var pl = getEngine().plan(); if (!pl) return;
        if (!sliding) { sliding = true; pushUndo(); }
        var beat = 60 / pl.bpmOut, frac = parseFloat(this.value) / 100;         // −50…+50 % of a beat
        getEngine().setVocalOffset(frac * beat, editWhich);
        pushTracks(); paintTimeline(); paintOffset(); paintPos();
    });
    $('mmOffset').addEventListener('change', function () {
        sliding = false;
        // `edge`: the nudge went to the end of its half beat — the move the beat buttons are for
        feature('offset', { ms: Math.round(getEngine().vocalOffset(editWhich) * 1000), src_a: (getEngine().song('a') || {}).tempoSrc || 'model', w: editWhich, src: 'slider', edge: Math.abs(parseFloat(this.value)) >= 49 ? 1 : 0 });
    });
    $('mmClean').addEventListener('change', function () {
        var on = this.checked;
        feature('clean', { on: on ? 1 : 0 });
        // the gate walks the whole vocal (≈ 0.45 s on 3 min — QA 24.09 caught it as a 456-ms INP):
        // the checkbox paints first, the work follows
        requestAnimationFrame(function () { setTimeout(function () { getEngine().applyClean(on); pushTracks(); paintTimeline(); }, 0); });
    });
    $('mmDuck').addEventListener('change', function () { mixer.set({ duck: this.checked ? 1 : 0 }); feature('duck', { on: this.checked ? 1 : 0 }); });
    $('mmFade').addEventListener('change', function () { mixer.set({ fade: this.checked }); });
    $('mmLoud').addEventListener('click', function (e) {
        var b = e.target.closest('[data-loud]'); if (!b) return;
        loud = b.dataset.loud; feature('mix_set', { p: 'loud', v: loud });
        Array.prototype.forEach.call(this.querySelectorAll('[data-loud]'), function (x) { x.classList.toggle('on', x === b); x.setAttribute('aria-pressed', x === b ? 'true' : 'false'); });
        try { window.__toolSettings && window.__toolSettings.save(SLUG, 'loud', loud); } catch (_) {}
        $('mmLoudNote').textContent = t('loud_note_' + loud);
    });

    // transport
    function setPlayUi(on) {
        var b = $('mmPlay'); $('mmPlayL').textContent = on ? t('pause') : t('play'); b.setAttribute('aria-pressed', on ? 'true' : 'false');
        var ip = b.querySelector('.mm-i-play'), ia = b.querySelector('.mm-i-pause'); if (ip) ip.hidden = !!on; if (ia) ia.hidden = !on;
    }
    /** iPhone: Web Audio is «ambient» by default, so the ring/silent switch mutes the mashup while a
     *  video in the next tab plays — a music tool the person starts on purpose plays as playback. */
    function playbackSession() { try { if (navigator.audioSession && navigator.audioSession.type !== 'playback') navigator.audioSession.type = 'playback'; } catch (_) {} }
    var waking = false;
    $('mmPlay').addEventListener('click', async function () {
        if (!mixer) return;
        // a tap while the device wakes up (or the mixer waits for its worklet) is a STOP, not a second play
        if (mixer.isPlaying() || waking) { waking = false; mixer.pause(); setPlayUi(false); return; }
        playbackSession();
        waking = true; setPlayUi(true);                  // at once: waking the audio device can take seconds
        try {
            if (window.__liveAudioCtxReady) {
                var c = await window.__liveAudioCtxReady(audioCtx());
                // a DIFFERENT context means the old one lost its device and is dead with every node on
                // it — adopt the new one, or Play starts sources on a context that will never sound
                if (c && c !== ctx) { ctx = c; getEngine().setContext(c); mixer.setContext(c); feature('ctx_adopted'); }
            }
        } catch (e) {
            waking = false; setPlayUi(false);
            // the primitive has already named the cause in a toast (device held by a DAW, a call…)
            if (!(e && e.__userNotified) && window.__handleError) window.__handleError(e, 'mashup-play');
            return;
        }
        if (!waking) return;                             // tapped again (stop) while the device woke up
        waking = false;
        if (check) stopCheck();                          // the tempo check and the mashup never sound together
        await mixer.play(); setPlayUi(mixer.isPlaying());
        feature('play', wsShownAt ? { first_ms: Math.round(performance.now() - wsShownAt) } : {});
        wsShownAt = 0;
    });
    $('mmToStart').addEventListener('click', function () { if (mixer) { mixer.seek(0); timeline.playhead(0); } });
    // Space plays and pauses the studio, as in every audio app — only while the studio is on screen
    // and never over a control that owns Space (a button, a box, a field, a slider)
    document.addEventListener('keydown', function (e) {
        if (e.key !== ' ' || e.repeat || e.ctrlKey || e.metaKey || e.altKey || !built || $('mmWs').hidden) return;
        var el = e.target, tag = el && el.tagName;
        if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || tag === 'BUTTON' || tag === 'A' || tag === 'SUMMARY' || (el && el.isContentEditable)) return;
        if (el && el.getAttribute && /^(slider|tab|button|checkbox)$/.test(el.getAttribute('role') || '') && el !== $('mmTl')) return;
        e.preventDefault();
        $('mmPlay').click();
        if (!spaceSaid) { spaceSaid = true; feature('space_play'); }
    });
    var spaceSaid = false;
    // the loop follows the vocal: moved by bars, beats or the offset, it loops where the vocal now is
    function syncLoop(tr) {
        if (!mixer) return;
        tr = tr || getEngine().tracks();
        var v = tr && (editWhich === 'c' && tr.vocal2 ? tr.vocal2 : tr.vocal);
        if ($('mmLoop').checked && v) mixer.setLoop(Math.max(0, v.at), v.at + v.buffer.duration);
        else mixer.setLoop(null);
    }
    function pushTracks() { var tr = getEngine().tracks(); mixer.setTracks(tr); syncLoop(tr); paintSpan(tr); if (reelBtn) paintReel(); }

    // «Mashup length»: the whole track, or only the part with the vocal — a bar before it comes in
    // to a bar after it ends — for the people who make a mashup for a 30-second video (the vocal
    // fragment already picks WHICH part; this decides how much of the music goes with it)
    var span = 'full';
    // The person's own edges of the mashup ('custom'): dragged on the music lane, kept as BAR LINES of the timeline
    // ({ b0, b1 }: indexes into edgeLines()) so a tempo or key change moves them with the bars
    var rangeBars = null;
    /** Where the music the mashup plays ends — its last clip (the body going round included). */
    function musicEnd(tr) {
        var MX = window.MashupMixer, k = tr && Object.keys(tr.stems).filter(function (x) { return x.indexOf('b:') === 0; })[0];
        return k && MX && MX.trackEnd ? MX.trackEnd(tr.stems[k]) : (tr ? tr.duration : 0);
    }
    /** The lines a mashup edge snaps to: the start of the file, every bar line, the end of the music. */
    function edgeLines(tr) {
        var bars = getEngine().timelineBars() || [], end = musicEnd(tr), out = [0];
        bars.forEach(function (b) { if (b > 0.05 && b < end - 0.05) out.push(b); });
        out.push(end);
        return out;
    }
    function rangeTimes(tr) {
        tr = tr || getEngine().tracks(); if (!tr || !rangeBars) return null;
        var L = edgeLines(tr), i0 = Math.min(rangeBars.b0, L.length - 2), i1 = Math.max(i0 + 1, Math.min(rangeBars.b1, L.length - 1));
        return [L[i0], L[i1]];
    }
    function nearestLine(L, t) { var bi = 0; for (var i = 1; i < L.length; i++) if (Math.abs(L[i] - t) < Math.abs(L[bi] - t)) bi = i; return bi; }
    /** An edge of the mashup dragged by dt: the line it lands on, as the range it makes and the distance drawn. */
    function rangeDrag(edge, dt) {
        var tr = getEngine().tracks(); if (!tr) return null;
        var L = edgeLines(tr), last = L.length - 1, cur = rangeTimes(tr) || [0, L[last]], i = nearestLine(L, cur[edge] + dt);
        var b0 = rangeBars ? rangeBars.b0 : 0, b1 = rangeBars ? rangeBars.b1 : last;
        if (edge === 0) b0 = Math.max(0, Math.min(i, b1 - 1)); else b1 = Math.min(last, Math.max(i, b0 + 1));
        return { b0: b0, b1: b1, last: last, dt: L[edge === 0 ? b0 : b1] - cur[edge] };
    }
    function onRange(m) {
        var r = rangeDrag(m.edge, m.dt);
        if (!r) return;
        var next = r.b0 === 0 && r.b1 === r.last ? null : { b0: r.b0, b1: r.b1 };
        if (JSON.stringify(next) === JSON.stringify(rangeBars)) return;
        pushUndo();
        // the whole music again is no range at all
        rangeBars = next;
        span = rangeBars ? 'custom' : 'full';
        paintSpanSeg(); paintSpan(); paintTimeline(); nextRefresh();
        feature('span', { v: span, edge: m.edge, bars: rangeBars ? rangeBars.b1 - rangeBars.b0 : null });
    }
    function paintSpanSeg() {
        Array.prototype.forEach.call(document.querySelectorAll('#mmSpan [data-span]'), function (x) { var on = x.dataset.span === span; x.classList.toggle('on', on); x.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    }
    function spanRange(tr) {
        if (span === 'custom') return rangeTimes(tr);
        tr = tr || getEngine().tracks(); if (span !== 'vocal' || !tr || !tr.vocal) return null;
        var pl = getEngine().plan(), B = getEngine().song('b');
        var bar = pl && pl.bpmOut > 0 ? ((B && B.grid && B.grid.meter) || 4) * 60 / pl.bpmOut : 2;
        // both singers of a three-song mashup: from the first one in to the last one out
        var v0 = tr.vocal.at, v1 = tr.vocal.at + tr.vocal.buffer.duration;
        if (tr.vocal2) { v0 = Math.min(v0, tr.vocal2.at); v1 = Math.max(v1, tr.vocal2.at + tr.vocal2.buffer.duration); }
        var t0 = Math.max(0, v0 - bar), t1 = Math.min(tr.duration, v1 + bar);
        return t1 - t0 >= 1 ? [t0, t1] : null;
    }
    function paintSpan(tr) {
        var r = spanRange(tr), note = $('mmSpanNote');
        if (!r) { note.hidden = true; return; }
        note.textContent = t(span === 'custom' ? 'span_note_custom' : 'span_note', { t: fmtTime(r[0]) + '–' + fmtTime(r[1]), d: fmtTime(r[1] - r[0]) });
        note.hidden = false;
    }
    $('mmSpan').addEventListener('click', function (e) {
        var b = e.target.closest('[data-span]'); if (!b) return;
        if (rangeBars) pushUndo();
        span = b.dataset.span === 'vocal' ? 'vocal' : 'full'; rangeBars = null;
        paintSpanSeg();
        try { window.__toolSettings && window.__toolSettings.save(SLUG, 'span', span); } catch (_) {}
        paintSpan(); if (timeline && built) paintTimeline();
        nextRefresh();
        feature('span', { v: span });
    });

    // «Что дальше» (tool-handoff.js): the mashup rides on to the next tool without a download and a
    // re-upload — a vertical clip for Reels/TikTok in /audio-to-video is the question people ask of a
    // mashup, and that tool already makes it (formats, lyrics, background). A LAZY card: the gates
    // need only a size and a name; the file is rendered on the tap by the path «Download» takes (the
    // same length choice, the same loudness), and always as WAV — the next tool re-encodes anyway.
    // ⚠️ The chain itself (mashup-maker → audio-to-video) is a line in config/tool-handoff.php and goes
    // in WITH the public flip: ToolHandoffConfigTest admits only public producers, and until the line
    // exists the rail component renders nothing — this mount then waits harmlessly.
    function handoffResult() {
        if (!built || !mixer) return null;
        var tr = getEngine().tracks(); if (!tr) return null;
        var rng = spanRange(tr), sec = rng ? rng[1] - rng[0] : tr.duration, sr = (ctx && ctx.sampleRate) || 48000;
        return { size: 44 + Math.round(sec * sr) * 4, type: 'audio/wav', filename: outName('mashup') + '.wav',
            make: async function () {
                await replanIdle();
                var opts = { loud: loud, range: spanRange() };
                if (opts.loud === 'match') opts.matchLufs = await musicLufs();
                var r = await renderMix(opts, { textContent: '' });
                feature('handoff', { sec: Math.round(r.buf.duration), span: opts.range ? 'vocal' : 'full' });
                var AP = window.AudioPost;
                return AP.encodeWavBitsAsync ? AP.encodeWavBitsAsync(r.buf, { bitDepth: 16, dither: true }) : new Blob([AP.encodeWav(r.buf)], { type: 'audio/wav' });
            } };
    }
    var nextRail = $('mmNext');
    if (nextRail && window.__toolHandoff) window.__toolHandoff.mount(nextRail, handoffResult);
    function nextRefresh() { if (nextRail && window.__toolHandoff && window.__toolHandoff.refresh) window.__toolHandoff.refresh(nextRail); }
    $('mmLoop').addEventListener('change', function () { syncLoop(); });
    $('mmZoomIn').addEventListener('click', function () { timeline.zoom(1); });
    $('mmZoomOut').addEventListener('click', function () { timeline.zoom(-1); });

    // ── downloads ──────────────────────────────────────────────────────────
    function setFmt(f) {
        fmt = f;
        Array.prototype.forEach.call(document.querySelectorAll('#mmFmt [data-fmt]'), function (x) { x.classList.toggle('on', x.dataset.fmt === f); x.setAttribute('aria-pressed', x.dataset.fmt === f ? 'true' : 'false'); });
        $('mmDlMain').textContent = t('dl_mashup') + ' (' + f.toUpperCase() + ')';
        $('mmKbpsWrap').hidden = f !== 'mp3';
    }
    $('mmFmt').addEventListener('click', function (e) {
        var b = e.target.closest('[data-fmt]'); if (!b) return;
        setFmt(b.dataset.fmt); feature('format', { fmt: fmt });
        try { window.__toolSettings && window.__toolSettings.save(SLUG, 'fmt', fmt); } catch (_) {}
    });
    /**
     * The master as MP3 in 10-s pieces. MP3Encoder.encode() copied the whole master for its worker
     * (114 MB for 5 min at 48 kHz) while the master itself lived — part of the export's 1.75 GB peak
     * with three songs on the phone profile (bug report 2026-09-28). A rate lamejs does not take, or
     * a stream that refuses to start or breaks half-way, goes the old way (encode() resamples on its own).
     */
    async function mp3Of(buf, kbps) {
        var E = window.MP3Encoder, ch = Math.min(2, buf.numberOfChannels), ctrl = null;
        if (E.encodeStream && ch >= 1) {
            try { ctrl = await E.encodeStream({ sampleRate: buf.sampleRate, channels: ch, bitrate: kbps, slug: SLUG }); } catch (_) { ctrl = null; }
        }
        if (!ctrl) return E.encode(buf, { bitrate: kbps, slug: SLUG });
        var N = buf.length, STEP = buf.sampleRate * 10, src = [];
        for (var c = 0; c < ch; c++) src.push(buf.getChannelData(c));
        try {
            for (var pos = 0; pos < N; pos += STEP) {
                var e = Math.min(N, pos + STEP), n = e - pos, back = ctrl.takePcm ? ctrl.takePcm() : null, piece = [];
                // a copy (feed() transfers it) — into the arrays the worker handed back with the last piece,
                // so a whole master leaves no garbage behind (a fresh pair per piece was 114 MB for 5 min)
                for (var c2 = 0; c2 < ch; c2++) {
                    var a = back && back[c2] && back[c2].length === n ? back[c2] : new Float32Array(n);
                    a.set(src[c2].subarray(pos, e));
                    piece.push(a);
                }
                await ctrl.feed(piece);
            }
            var done = await ctrl.finish(), blob = done && done.blob;
            if (!blob || !blob.size) throw new Error('empty mp3');
            try { if (window.__audioSourceTags) blob = await window.__audioSourceTags.patchBlob(blob, 'out.mp3'); } catch (_) {}
            return blob;
        } catch (err) {
            try { ctrl.abort(); } catch (_) {}
            // a stream that broke half-way (a piece past its deadline on a starved device) is not the end
            // of the export: the whole master goes the old way — one message, watched by its heartbeat
            feature('mp3_stream_fallback', { err: String((err && (err.code || err.message)) || err).slice(0, 80) });
            return E.encode(buf, { bitrate: kbps, slug: SLUG });
        }
    }
    async function encode(buf) {
        if (fmt === 'mp3' && window.MP3Encoder) return mp3Of(buf, parseInt($('mmKbps').value, 10) || 320);
        if (fmt === 'flac' && window.__ensureFFmpeg) {
            try {
                await window.__ensureFFmpeg();
                var wav = new Blob([window.AudioPost.encodeWav(buf)], { type: 'audio/wav' });
                // process() answers { blob, name, url, size } — handing that object to the download
                // door was refused as unreadable and the FLAC button delivered nothing (e2e, 23.09.2026)
                var out = await window.FFmpegProcessor.process(wav, ['-c:a', 'flac', '-compression_level', '5'], 'mashup.flac', 'input.wav');
                if (out && out.url) { try { URL.revokeObjectURL(out.url); } catch (_) {} }
                if (out && out.blob && out.blob.size) return out.blob;
            } catch (e) { try { console.warn('[mashup flac]', e); } catch (_) {} window.__toast(t('err_failed'), 'warning'); }
        }
        var AP = window.AudioPost;
        if (AP && AP.encodeWavBitsAsync) return AP.encodeWavBitsAsync(buf, { bitDepth: 16, dither: true });
        return new Blob([AP.encodeWav(buf)], { type: 'audio/wav' });
    }
    function extOf(blob) { return fmt === 'flac' && blob && /flac/.test(blob.type || 'flac') ? 'flac' : fmt === 'mp3' ? 'mp3' : fmt === 'flac' ? 'flac' : 'wav'; }
    function outName(kind) {
        var a = getEngine().song('a'), b = getEngine().song('b'), c = withC() ? getEngine().song('c') : null;
        var cap = c ? 40 : 60, base = function (s) { return String(s && s.name || '').replace(/\.[^.]+$/, '').slice(0, cap); };
        var ON = window.__outputName, all = base(a) + ' × ' + base(b) + (c ? ' × ' + base(c) : '');
        var name = kind === 'mashup' ? all + ' (' + t('file_mashup') + (spanRange() ? ', ' + t('file_clip') : '') + ')'
            : kind === 'reel' ? all + ' (' + t('file_mashup') + ', ' + t('file_clip') + ')'
            : kind === 'stems' ? all + ' (' + t('file_stems') + ')'
            : kind === 'acapella' ? base(a) + (c ? ' × ' + base(c) : '') + ' (' + t('file_acapella') + ')' : base(b) + ' (' + t('file_music') + ')';
        try { if (ON && ON.sanitize) name = ON.sanitize(name); } catch (_) {}
        return name;
    }
    function busyBtn(btn, text, fn) {
        if (btn.disabled) return;
        var html = btn.textContent; btn.disabled = true; btn.textContent = text;
        // a file is always the sound the controls show: a key or tempo still rendering is waited for
        Promise.resolve().then(replanIdle).then(fn).catch(function (e) {
            try { console.error('[mashup export]', e); } catch (_) {}
            try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'export_failed', btn: btn.id, fmt: fmt, err: String(e && e.message || e).slice(0, 140) }); } catch (_) {}
            window.__handleError(new Error(t('err_export')), SLUG);
        }).then(function () { btn.disabled = false; btn.textContent = html; });
    }
    // The last master is reused while nothing under it changed: a second format is an encode, not a
    // re-render (a 3:43 master took 42 s to render and master on a desktop). One slot only — a 3:43
    // stereo master is ~85 MB, so phones and ≤ 4 GB devices render again instead of holding it.
    var mixCache = null, lufsCache = null;
    var canCacheMix = !((navigator.deviceMemory && navigator.deviceMemory <= 4) || isMobile());
    // the music song's own level («как у музыки»), by FILE: a phone lets go of the song's sound once the
    // server's stems are in (MashupEngine releaseSongs), so it is measured before — after B's analysis
    // and at the build's start — and a later export reads the number, not the song
    async function musicLufs() {
        var b = getEngine().song('b'); if (!b) return null;
        var key = b.fileKey || b.name;
        if (lufsCache && lufsCache.key === key) return lufsCache.v;
        if (lufsCache && lufsCache.key === key + '#pending') return lufsCache.p;
        var p = (async function () {
            var buf = b.buffer, v = null;
            if (!buf) { try { buf = await getEngine().ensureBuffer('b'); } catch (_) { buf = null; } }
            try { if (buf && window.LoudnessR128 && window.LoudnessR128.measureIntegratedLUFSAsync) v = await window.LoudnessR128.measureIntegratedLUFSAsync(buf); } catch (_) {}
            return v;
        })();
        lufsCache = { key: key + '#pending', p: p };
        var v = await p;
        if (lufsCache && lufsCache.key === key + '#pending') lufsCache = { key: key, v: v };
        return v;
    }
    async function renderMix(opts, btn) {
        var key = JSON.stringify([mixer.version(), mixer.state(), opts.on || null, opts.loud, opts.matchLufs == null ? null : Math.round(opts.matchLufs * 100), opts.range || null]);
        if (mixCache && mixCache.key === key) return { buf: mixCache.buf, cached: true };
        var label = t('rendering');
        var buf = await mixer.render(Object.assign({}, opts, { onProgress: function (p) { btn.textContent = label + ' ' + fmtPct(p); } }));
        mixCache = canCacheMix ? { key: key, buf: buf } : null;
        return { buf: buf, cached: false };
    }
    async function exportMix(btn, what, opts) {
        var t0 = performance.now();
        if (opts.loud === 'match') opts.matchLufs = await musicLufs();
        var r = await renderMix(opts, btn), t1 = performance.now();
        btn.textContent = t('encoding');
        var blob = await encode(r.buf), t2 = performance.now();
        window.__downloadFile(blob, outName(what) + '.' + extOf(blob));
        var lim = r.buf.__limit || {};
        feature('download', { what: what, fmt: fmt, loud: opts.loud, span: opts.range ? 'vocal' : 'full', sec: Math.round(r.buf.duration), gr: lim.maxGrDb != null ? lim.maxGrDb : null, pre: lim.preGainDb != null ? lim.preGainDb : null,
            cached: r.cached ? 1 : 0, ms_mix: Math.round(t1 - t0), ms_enc: Math.round(t2 - t1), tm: r.cached ? null : (r.buf.__timing || null) });
    }
    $('mmDlMain').addEventListener('click', function () {
        var btn = this;
        busyBtn(btn, t('rendering'), function () { return exportMix(btn, 'mashup', { loud: loud, range: spanRange() }); });
    });
    $('mmDlVocal').addEventListener('click', function () {
        var btn = this;
        busyBtn(btn, t('encoding'), async function () {
            // the fitted vocal, padded with silence from 0:00 so it lines up with the music file in a DAW
            // a vocal moved before 0:00 (by beats or by the offset) loses its head, exactly as the player plays it;
            // a three-song mashup gives both singers in one file, each where it sings
            var tr = getEngine().tracks(), vs = [tr.vocal].concat(tr.vocal2 ? [tr.vocal2] : []), sr = tr.vocal.buffer.sampleRate, total = 1;
            var parts = vs.map(function (v) {
                var lead = Math.max(0, Math.round(v.at * sr)), skip = Math.min(v.buffer.length - 1, Math.max(0, Math.round(-v.at * sr)));
                total = Math.max(total, lead + v.buffer.length - skip);
                return { v: v, lead: lead, skip: skip };
            });
            var out = new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(2, total, sr).createBuffer(2, total, sr);
            parts.forEach(function (p) {
                for (var c = 0; c < 2; c++) {
                    var src = p.v.buffer.getChannelData(Math.min(c, p.v.buffer.numberOfChannels - 1)), d = out.getChannelData(c);
                    for (var i = p.skip, j = p.lead; i < src.length && j < total; i++, j++) d[j] += src[i];
                }
            });
            var blob = await encode(out);
            window.__downloadFile(blob, outName('acapella') + '.' + extOf(blob));
            feature('download', { what: 'acapella', fmt: fmt, c: tr.vocal2 ? 1 : 0 });
        });
    });
    $('mmDlMusic').addEventListener('click', function () {
        var btn = this;
        busyBtn(btn, t('rendering'), function () { return exportMix(btn, 'music', { loud: 'stream', on: { 'a:vocals': false, 'c:vocals': false } }); });
    });

    // ── share: straight from the phone to a messenger ─────────────────────────────────────
    // A mashup is made to be sent. The share sheet needs a FRESH tap (the render takes seconds and
    // outlives the tap's activation — iOS refuses a share after that wait), so it is two taps: the
    // first prepares the file, the second hands it to the sheet. MP3 whatever the download format:
    // a 40-MB WAV is the wrong thing to post into a chat. Shown only where the browser can share an
    // audio FILE (Chrome on Android/Windows/macOS, Safari on iPhone and Mac — not Firefox).
    var prepared = null, sharing = false;
    var canShareAudio = (function () {
        try { return !!(navigator.share && navigator.canShare && typeof File === 'function' && navigator.canShare({ files: [new File([new Uint8Array(8)], 'mashup.mp3', { type: 'audio/mpeg' })] })); } catch (_) { return false; }
    })();
    function shareKey() {
        if (!mixer) return null;
        return JSON.stringify([mixer.version(), mixer.state(), loud, spanRange(), $('mmKbps').value]);
    }
    // any control in the studio may change the sound: the prepared file is then stale, and the
    // button says «Share» (prepare again) instead of «Send the file» — one listener, not one per control
    var shareRaf = 0;
    ['input', 'change', 'click'].forEach(function (ev) {
        $('mmWs').addEventListener(ev, function () { cancelAnimationFrame(shareRaf); shareRaf = requestAnimationFrame(paintShare); });
    });
    function paintShare() {
        var b = $('mmShare'); if (!b) return;
        b.hidden = !canShareAudio || !built;
        if (b.disabled) return;                          // preparing: the label carries the progress
        var ready = !!(prepared && prepared.key === shareKey());
        $('mmShareL').textContent = ready ? t('share_ready') : t('share');
        b.classList.toggle('ghost', !ready);
    }
    $('mmShare').addEventListener('click', async function () {
        var btn = this;
        if (!canShareAudio || !built || btn.disabled) return;
        if (prepared && prepared.key === shareKey()) {
            // the tap that shares: no await before navigator.share, or the sheet is refused
            var file = prepared.file;
            sharing = true;
            navigator.share({ files: [file], title: file.name.replace(/\.mp3$/i, '') }).then(function () {
                feature('share', { stage: 'sent', sec: prepared ? prepared.sec : null });
            }, function (e) {
                if (e && e.name === 'AbortError') { feature('share', { stage: 'cancel' }); return; }
                feature('share', { stage: 'fail', err: String(e && (e.name || e.message) || e).slice(0, 60) });
                window.__handleError(new Error(t('err_share')), SLUG);
            }).then(function () { sharing = false; });
            return;
        }
        // the first tap: render and encode, the label (not the icon) carries the progress
        var lbl = { set textContent(v) { var s = $('mmShareL'); if (s) s.textContent = v; } };
        btn.disabled = true; lbl.textContent = t('share_prep');
        try {
            await replanIdle();
            var key = shareKey(), opts = { loud: loud, range: spanRange() };
            if (opts.loud === 'match') opts.matchLufs = await musicLufs();
            var r = await renderMix(opts, lbl);
            lbl.textContent = t('encoding');
            var blob = window.MP3Encoder ? await mp3Of(r.buf, parseInt($('mmKbps').value, 10) || 192) : null;
            if (!blob) throw new Error('no mp3 encoder');
            prepared = { key: key, sec: Math.round(r.buf.duration), file: new File([blob], outName('mashup') + '.mp3', { type: 'audio/mpeg' }) };
            feature('share', { stage: 'ready', sec: prepared.sec, cached: r.cached ? 1 : 0 });
        } catch (e) {
            try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'export_failed', btn: 'mmShare', err: String(e && e.message || e).slice(0, 140) }); } catch (_) {}
            window.__handleError(new Error(t('err_export')), SLUG);
        } finally {
            btn.disabled = false; paintShare();
            if (prepared) { try { btn.focus({ preventScroll: true }); } catch (_) {} }
        }
    });

    // ── the stems pack: every fitted track as its own file, for a DAW or a DJ set ─────────
    // Each file starts at the mashup's 0:00 and runs its full length, so dropped into any DAW they
    // line up with one another and with the mix: the vocal dry (no reverb, no EQ — like «the fitted
    // vocal»), the music stems in the mashup's key and tempo. The take-away artefact is what the
    // plan sells (config/tiers.php `mashup_stems_pack`, the /chord-finder line); where no plan is
    // sold on this locale the lane stays open. A refusal says what it is and never leaves the page.
    function isPaid() {
        var tl = window.__tierLimits;
        if (!tl) return false;
        if (tl.is_pro) return true;
        return !!(tl.features && PAID.feature && tl.features.indexOf(PAID.feature) >= 0);
    }
    function stemsOpen() { return !PAID.sells || isPaid(); }
    function paintStemsLock() {
        var b = $('mmDlStems'); if (!b) return;
        var open = stemsOpen(), lock = b.querySelector('.mm-lock');
        if (!open && !lock) { lock = document.createElement('span'); lock.className = 'mm-lock'; lock.textContent = PAID.planName || ''; b.appendChild(lock); }
        if (lock) lock.hidden = open;
        if (open) $('mmStemsPerk').hidden = true;
    }
    document.addEventListener('tier-bootstrap:ready', paintStemsLock);
    var STEM_NAME = { 'c:vocals': 'stem_vocals', 'a:vocals': 'stem_vocals', 'a:drums': 'stem_drums', 'a:bass': 'stem_bass', 'a:other': 'stem_other', 'b:vocals': 'stem_vocals', 'b:drums': 'stem_drums', 'b:bass': 'stem_bass', 'b:other': 'stem_other' };
    /** The tracks that sound now, each laid onto the mashup's timeline from 0:00 (the vocal as its
     *  own dry track). A muted stem is not in the pack: the pack is the mix, taken apart. */
    function stemJobs() {
        var tr = getEngine().tracks(), st = mixer.state(), jobs = [];
        if (!tr) return jobs;
        if (tr.vocal && st.on['a:vocals'] !== false) jobs.push({ key: 'a:vocals', src: tr.vocal });
        if (tr.vocal2 && st.on['c:vocals'] !== false) jobs.push({ key: 'c:vocals', src: tr.vocal2 });
        Object.keys(tr.stems).forEach(function (k) {
            var on = st.on[k] != null ? st.on[k] : defaultOn(k);
            if (on) jobs.push({ key: k, src: tr.stems[k] });
        });
        return jobs;
    }
    function placeOnTimeline(src, total, sr) {
        var OC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
        var len = Math.max(1, Math.round(total * sr)), out = new OC(2, len, sr).createBuffer(2, len, sr);
        // the player's own clips and seams: a music stem that goes round is in the pack as it was heard
        return window.MashupMixer.layTrack(src, out, sr);
    }
    $('mmDlStems').addEventListener('click', async function () {
        var btn = this;
        if (btn.disabled || !built || !mixer) return;
        if (!stemsOpen()) {
            var perk = $('mmStemsPerk');
            perk.innerHTML = t('stems_perk', { plan: PAID.planName || '' }) + (PAID.ctaUrl ? ' <a href="' + PAID.ctaUrl + '">' + t('stems_cta', { plan: PAID.planName || '' }) + '</a>' : '');
            perk.hidden = false;
            feature('stems_pack', { stage: 'blocked' });
            return;
        }
        var BD = window.__batchDeliver;
        if (!BD || !BD.openBatch) { window.__handleError(new Error(t('err_export')), SLUG); return; }
        var jobs = stemJobs(), tr = getEngine().tracks(), sr = (ctx && ctx.sampleRate) || 48000;
        if (!jobs.length || !tr) return;
        var bpm = getEngine().plan() ? Math.round(getEngine().plan().bpmOut) : null;
        var total = jobs.length * (44 + Math.round(tr.duration * sr) * 4), batch = null, label = btn.innerHTML;
        // the batch first — a folder picker lives on this tap's activation, and nothing may be awaited before it
        try { batch = await BD.openBatch({ zipName: outName('stems') + '.zip', slug: SLUG, total: total }); }
        catch (e) { if (!(e && e.name === 'AbortError')) window.__handleError(new Error(t('err_export')), SLUG); return; }
        if (!batch) return;
        btn.disabled = true;
        var t0 = performance.now(), n = 0;
        try {
            await replanIdle();                              // the pack is the key and tempo the chips show
            jobs = stemJobs(); tr = getEngine().tracks();
            for (var i = 0; i < jobs.length; i++) {
                btn.textContent = t('stems_progress', { n: i + 1, of: jobs.length });
                var j = jobs[i], who = j.key.charAt(0).toUpperCase(), buf = placeOnTimeline(j.src, tr.duration, sr);
                var AP = window.AudioPost, blob = AP.encodeWavBitsAsync ? await AP.encodeWavBitsAsync(buf, { bitDepth: 16, dither: true }) : new Blob([AP.encodeWav(buf)], { type: 'audio/wav' });
                // a ready instrumental is ONE track, the whole music — not «other instruments»
                var sb = getEngine().song('b'), label = j.key === 'b:other' && sb && sb.isStem ? 'lane_music' : (STEM_NAME[j.key] || 'stem_other');
                var name = (i + 1) + ' ' + who + ' - ' + t(label) + (bpm ? ' (' + bpm + ' BPM)' : '');
                try { if (window.__outputName && window.__outputName.sanitize) name = window.__outputName.sanitize(name); } catch (_) {}
                await batch.write(name + '.wav', blob);
                buf = null; blob = null; n++;
            }
            await batch.finish();
            feature('stems_pack', { stage: 'done', n: n, via: batch.mode || null, sec: Math.round(tr.duration), ms: Math.round(performance.now() - t0) });
        } catch (e) {
            try { if (batch.abort) batch.abort(); } catch (_) {}
            try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'export_failed', btn: 'mmDlStems', n: n, err: String(e && e.message || e).slice(0, 140) }); } catch (_) {}
            window.__handleError(new Error(t('err_export')), SLUG);
        } finally {
            btn.disabled = false; btn.innerHTML = label; paintStemsLock();
        }
    });

    // ── «Подобрать пару из своих песен» (mm-pair.js) ───────────────────────────
    // A pair goes into the decks by the uploader's own door, like a swap: the decks analyse it in full.
    var pairF = null;
    (function () {
        var zone = $('mmPairUp'); if (!zone || !window.MashupPair) return;
        var P = CFG.pair || {};
        pairF = window.MashupPair.create({
            els: { zone: zone, songs: $('mmPairSongs'), list: $('mmPairList'), status: $('mmPairStatus'), cap: $('mmPairCap'), more: $('mmPairMore'), clear: $('mmPairClear'), swapNote: $('mmPairSwap') },
            t: t, feature: feature, ctx: audioCtx,
            cap: Math.max(2, (P.cap < 2 && isPaid()) ? (P.paid || 2) : (P.cap || 4)), paid: P.paid || 50, plan: P.plan || '', ctaUrl: P.ctaUrl || '',
            onPick: function (fa, fb) {
                if (!window.ToolUploader || !window.ToolUploader.inject) return;
                $('mmStemA').checked = false; $('mmStemB').checked = false;
                window.ToolUploader.inject('mmUpA', fa, 'pair');
                window.ToolUploader.inject('mmUpB', fb, 'pair');
                var d = $('mmPair'); if (d) d.open = false;
                try { deckEl('a').scrollIntoView({ block: 'nearest' }); } catch (_) {}
            }
        });
        var take = function (files) { if (files && files.length) pairF.add(Array.prototype.slice.call(files)); };
        // Through the uploader's binder: it hands over songs picked before this ran
        // and takes them once. (A plain listener plus a look at the zone's stamp
        // took an early pick twice once the zone began redelivering to late listeners.)
        var onSongs = function (f, detail) { take((detail && detail.files) || (f ? [f] : null)); };
        if (typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(zone, onSongs);
        else zone.addEventListener('tool-uploader:file', function (e) { onSongs(e.detail && e.detail.file, e.detail); });
        var d = $('mmPair');
        if (d) d.addEventListener('toggle', function () { if (d.open) feature('pair_open'); });
    })();

    // ── «Видео для Reels»: the loudest-vocal ~30 s, in /audio-to-video's vertical template ──
    // The clip is whole bars (a multiple of four, 20–40 s) where the singers are loudest, starting a bar
    // before a phrase of the landing; it rides to /audio-to-video through the site's hand-off with a
    // hint — the «Reels» template and the songs' names as the title — so one press ends on a vertical
    // video with its words, not on an empty editor. Rendered only on the press (the lazy card).
    function reelRange(tr) {
        var E = getEngine(), pl = E.plan(), B = E.song('b');
        tr = tr || E.tracks();
        if (!tr || !pl || !B || !(pl.bpmOut > 0)) return null;
        var bars = E.timelineBars();                               // the repeated bars of a music going round included
        if (bars.length < 2) return null;
        var barSec = (bars[bars.length - 1] - bars[0]) / (bars.length - 1);
        var L = Math.max(4, Math.round(30 / barSec / 4) * 4);
        while (L > 4 && L * barSec > 40) L -= 4;
        while (L * barSec < 20) L += 4;
        var vs = [tr.vocal].concat(tr.vocal2 ? [tr.vocal2] : []), en = new Float64Array(bars.length);
        vs.forEach(function (v) {
            var d = v.buffer.getChannelData(0), sr = v.buffer.sampleRate;
            for (var b = 0; b < bars.length; b++) {
                var i0 = Math.max(0, Math.floor((bars[b] - v.at) * sr)), i1 = Math.min(d.length, Math.floor(((b + 1 < bars.length ? bars[b + 1] : bars[b] + barSec) - v.at) * sr)), s = 0;
                for (var i = i0; i < i1; i += 64) s += d[i] * d[i];
                en[b] += s;
            }
        });
        var lead = pl.o - 1, best = -1, bestScore = -1;
        for (var s0 = 0; s0 + L <= bars.length; s0++) {
            var sum = 0; for (var k = s0; k < s0 + L; k++) sum += en[k];
            var score = sum * ((((s0 - lead) % 4) + 4) % 4 === 0 ? 1 : 0.9);
            if (score > bestScore) { bestScore = score; best = s0; }
        }
        if (best < 0) return [0, tr.duration];                     // the music is shorter than the clip
        return [Math.max(0, bars[best]), Math.min(tr.duration, best + L < bars.length ? bars[best + L] : tr.duration)];
    }
    function cleanTitle(n) {
        return String(n || '').replace(/\.[^.]+$/, '').replace(/_+/g, ' ')
            .replace(/[(\[][^)\]]*(acap+ell?a|a cappella|instrumental|karaoke|minus|минус|акапелла|vocals?|bpm|official|audio|lyrics?)[^)\]]*[)\]]/ig, '')
            .replace(/\b\d{2,3}(?:[.,]\d+)?\s*bpm\b/ig, '').replace(/\s{2,}/g, ' ').trim();
    }
    /** The card's words: the songs' titles (tags first, the file name cleaned otherwise), the artists under them. */
    function reelHints() {
        var E = getEngine(), ks = ['a', 'b'].concat(withC() ? ['c'] : []), titles = [], artists = [];
        ks.forEach(function (k) {
            var s = E.song(k), tg = tagCache[keys[k]] || {};
            titles.push(tg.title || cleanTitle(s && s.name) || k.toUpperCase());
            if (tg.artist && artists.indexOf(tg.artist) < 0) artists.push(tg.artist);
        });
        var title = titles.join(' × ');
        if (title.length > 60) title = titles.map(function (x) { return x.length > 26 ? x.slice(0, 25) + '…' : x; }).join(' × ');
        return { template: 'reel', title: title.slice(0, 100), sub: (artists.length ? artists.join(' × ') : t('reel_sub')).slice(0, 200) };
    }
    var reelBtn = $('mmReel');
    function reelResult() {
        if (!built || !mixer) return null;
        var tr = getEngine().tracks(), rng = tr && reelRange(tr); if (!rng) return null;
        var sr = (ctx && ctx.sampleRate) || 48000;
        return { size: 44 + Math.round((rng[1] - rng[0]) * sr) * 4, type: 'audio/wav', filename: outName('reel') + '.wav', hints: reelHints(),
            make: async function () {
                var lbl = { set textContent(v) { var s = $('mmReelL'); if (s) s.textContent = v; } };
                lbl.textContent = t('reel_prep');
                await replanIdle();
                var opts = { loud: loud, range: reelRange() };
                if (opts.loud === 'match') opts.matchLufs = await musicLufs();
                var r = await renderMix(opts, lbl);
                feature('reel', { stage: 'made', sec: Math.round(r.buf.duration), from: Math.round(opts.range[0]), c: getEngine().tracks().vocal2 ? 1 : 0, cached: r.cached ? 1 : 0 });
                var AP = window.AudioPost;
                return AP.encodeWavBitsAsync ? AP.encodeWavBitsAsync(r.buf, { bitDepth: 16, dither: true }) : new Blob([AP.encodeWav(r.buf)], { type: 'audio/wav' });
            } };
    }
    function paintReel() {
        if (!reelBtn) return;
        var can = !!(window.__toolHandoff && (!window.__toolHandoff.available || window.__toolHandoff.available()));
        reelBtn.hidden = !built || !can;
        var rng = built ? reelRange() : null, s = $('mmReelL');
        if (s && !reelBtn.getAttribute('aria-busy')) s.textContent = rng ? t('reel_btn', { d: fmtTime(rng[1] - rng[0]) }) : t('reel_btn', { d: '0:30' });
    }
    if (reelBtn) {
        reelBtn.addEventListener('click', function () { feature('reel', { stage: 'click' }); }, true);
        if (window.__toolHandoff && window.__toolHandoff.carry) window.__toolHandoff.carry(reelBtn, reelResult);
    }

    // the note under the button: two lines until asked; the «More» link exists only while it cuts something
    (function () {
        var hint = $('mmGoHint'), more = $('mmGoMore'); if (!hint || !more) return;
        // the page is the same for everyone (page cache); the beat model is not: a phone and a browser
        // without a graphics card download the 5 MB one, not the 40 MB — the note says the size they get
        try {
            var H = CFG.hintMb, An = window.MashupAnalysis, which = An && An.planned ? An.planned() : null;
            if (H && which && H[which] && T.model_hint) hint.textContent = t('model_hint', { beat: H[which], sep: H.sep });
        } catch (_) {}
        function fit() { if (!hint.classList.contains('open')) more.hidden = hint.scrollHeight <= hint.clientHeight + 1; }
        more.addEventListener('click', function () {
            var open = !hint.classList.contains('open');
            hint.classList.toggle('open', open); more.setAttribute('aria-expanded', open ? 'true' : 'false');
            more.hidden = open;
        });
        if (window.ResizeObserver) new ResizeObserver(fit).observe(hint); else window.addEventListener('resize', fit);
        fit();
    })();

    // ── boot ───────────────────────────────────────────────────────────────
    try {
        var sf = window.__toolSettings && window.__toolSettings.load(SLUG, 'fmt', 'wav'); if (sf === 'mp3' || sf === 'flac' || sf === 'wav') fmt = sf;
        var sl = window.__toolSettings && window.__toolSettings.load(SLUG, 'loud', 'match'); if (sl === 'stream' || sl === 'club' || sl === 'match') loud = sl;
        var ss = window.__toolSettings && window.__toolSettings.load(SLUG, 'span', 'full'); if (ss === 'vocal') span = ss;
    } catch (_) {}
    setFmt(fmt);
    Array.prototype.forEach.call(document.querySelectorAll('#mmSpan [data-span]'), function (x) { x.classList.toggle('on', x.dataset.span === span); x.setAttribute('aria-pressed', x.dataset.span === span ? 'true' : 'false'); });
    Array.prototype.forEach.call(document.querySelectorAll('#mmLoud [data-loud]'), function (x) { x.classList.toggle('on', x.dataset.loud === loud); x.setAttribute('aria-pressed', x.dataset.loud === loud ? 'true' : 'false'); });
    $('mmLoudNote').textContent = t('loud_note_' + loud);
    try { window.__toolSettings && window.__toolSettings.bind(SLUG, $('mmKbps'), 'kbps', '128'); } catch (_) {}
    syncGo();
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { probeSupport(0); srvRestore(); }); else { probeSupport(0); srvRestore(); }
})();
